import React, {
  useState,
  useCallback,
  useEffect,
  ReactNode,
  useRef,
  useMemo,
  Fragment,
} from "react";

import { useDispatch } from "react-redux";
import { CssFieldDefinition } from "./";
import {
  AngleIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  BlendingModeIcon,
  BorderAllIcon,
  BorderBottomIcon,
  BorderLeftIcon,
  BorderRightIcon,
  BorderSplitIcon,
  BorderStyleIcon,
  BorderTopIcon,
  ColumnSpacingIcon,
  CornerBottomLeftIcon,
  CornerBottomRightIcon,
  CornerTopLeftIcon,
  CornerTopRightIcon,
  CornersIcon,
  CursorArrowIcon,
  FontBoldIcon,
  FontFamilyIcon,
  FontItalicIcon,
  FontSizeIcon,
  LetterSpacingIcon,
  LineHeightIcon,
  MagicWandIcon,
  OpacityIcon,
  OverlineIcon,
  PinBottomIcon,
  PinLeftIcon,
  PinRightIcon,
  PinTopIcon,
  RowSpacingIcon,
  ShadowIcon,
  StrikethroughIcon,
  TextAlignCenterIcon,
  TextAlignJustifyIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
  UnderlineIcon,
} from "@radix-ui/react-icons";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import "highlight.js/styles/github.css";
import { sendMessageToIFrame, setIFrameChanged } from "@/store/iframeSlice";
import { TAILWIND_PROPERTIES } from "@/lib/tailwind";
import {
  PropertyType,
  useTailwindProperties,
  useUnknownTailwindClasses,
} from "@/hooks/useTailwindProperties";
import { usePropertyState } from "./TailwindStylingPanel/hooks/usePropertyState";
import { MaxWidthIcon, MinWidthIcon } from "../Core/Icons";
import { ClassType } from "@/lib/tailwind/properties";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { FixedSizeList as List } from "react-window";
import hljs from "highlight.js";
import { DELETE_STYLE_CONSTANT } from "@/utils/utils";
import { Tooltip } from "react-tooltip";

import {
  AlignContentCenterIcon,
  AlignContentFlexEndIcon,
  AlignContentFlexStartIcon,
  AlignContentSpaceAroundIcon,
  AlignContentSpaceBetweenIcon,
  AlignContentStretchIcon,
  AlignItemsBaseline,
  AlignItemsCenterIcon,
  AlignItemsEndIcon,
  AlignItemsStartIcon,
  AlignItemsStretchIcon,
  JustifyCenterIcon,
  JustifyFlexEndIcon,
  JustifyFlexStartIcon,
  JustifySpaceBetweenIcon,
  JustifySpaceEvenlyIcon,
  NoWrapIcon,
  PaddingBottomIcon,
  PaddingHorizontalIcon,
  PaddingLeftIcon,
  PaddingRightIcon,
  PaddingTopIcon,
  PaddingVerticalIcon,
  WrapIcon,
  WrapReverseIcon,
  PlusIcon,
  MinusIcon,
  ExpandSettingPadding,
} from "@/components/RightStyle/TailwindStylingPanel/components/icons";
import { cn } from "@/lib/utils";
import _ from "lodash";
import {
  ChevronsLeftRightIcon,
  ChevronsRightLeftIcon,
  MoveHorizontalIcon,
} from "lucide-react";
import { TempoElement } from "@/utils/tempoElement";
import {
  AddClassChange,
  RemoveClassChange,
} from "@/store/zustand/changeLedgerTypes";
import { useChangeLedgerStore } from "@/store/zustand/changeLedger";
import { ALL_MODIFIERS, ALL_MODIFIERS_SET } from "@/hooks/useModifiers";
import {
  AutoLayoutSelector,
  ClickableIcon,
  ComboBoxOptionType,
  DiscreteInput,
  DiscreteOption,
  DraggableInput,
  DropdownInput,
  DropdownOptionType,
  ToggleInput,
  EmptyInputState,
  ComboboxContainer,
} from "@/components/RightStyle/TailwindStylingPanel/components/input";
import {
  ModalContainer,
  ModalState,
  StylingModalType,
  modalTypeToTitle,
  FillModal,
} from "@/components/RightStyle/TailwindStylingPanel/components/modals";
import { safeParseCssStringAsInlineStyles } from "./TailwindStylingPanel/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import CustomColorPicker from "./TailwindStylingPanel/components/input/CustomColorPicker";

// import useStylingStore from "@/store/zustand/styling/store";

interface Props {
  cssValues: { [cssName: string]: CssFieldDefinition };
  parentCssEvals: { [cssName: string]: string };
  applyCssChanges: (
    changes: any,
    modifiers: string[],
    customProperties: ClassType[]
  ) => void;
  selectedElement: TempoElement;
  multiselectedElements: TempoElement[];
  selectedTreeElement: any;
  classList: string[];
  modifiers: string[];
  addModifier: (modifier: string) => void;
  removeModifier: (modifier: string) => void;
  modifierTally: { [modifier: string]: number };
}

const TailwindStylingPanel: React.FC<Props> = ({
  selectedElement,
  multiselectedElements,
  selectedTreeElement,
  cssValues,
  parentCssEvals,
  applyCssChanges,
  classList,
  modifiers,
  addModifier,
  removeModifier,
  modifierTally,
}) => {
  const dispatch = useDispatch<any>();
  const addChange = useChangeLedgerStore((state) => state.addChange);

  const [modal, setModal] = useState<ModalState>({
    type: StylingModalType.None,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const borderModalRef = useRef<HTMLDivElement>(null);
  const fillModalRef = useRef<HTMLDivElement>(null);
  const textColorModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setModal({
      type: StylingModalType.None,
    });
  }, [selectedElement]);

  const [recentClassesCache, setRecentClassesCache] = useState({});

  useEffect(() => {
    setRecentClassesCache({});
  }, [selectedElement]);

  const classListWithCache = useMemo(() => {
    if (classList == null) {
      return [];
    }
    // Classes set to be added that are not in the cache
    const classesToBeAdded =
      Object.keys(recentClassesCache).filter((cls) => {
        return recentClassesCache[cls] === true && !classList?.includes(cls);
      }) || [];

    // Classes set to be removed that are in the cache
    const classesToBeRemoved = Object.keys(recentClassesCache).filter((cls) => {
      return recentClassesCache[cls] === false && classList?.includes(cls);
    });

    if (!classList) {
      return classesToBeAdded;
    }

    return classList
      .filter((cls) => !classesToBeRemoved.includes(cls))
      .concat(classesToBeAdded);
  }, [classList, recentClassesCache]);

  const unknownTailwindClasses = useUnknownTailwindClasses(classListWithCache);

  const unknownTailwindClassesToShow = useMemo(() => {
    const removableClasses = new Set(
      selectedTreeElement?.removableClasses || []
    );

    return unknownTailwindClasses.filter((cls) => {
      return removableClasses.has(cls);
    });
  }, [unknownTailwindClasses, selectedTreeElement]);

  // Ignore the styles from the current modifiers, if they are set.
  const totalModifierTally = useMemo(() => {
    let total = 0;
    Object.keys(modifierTally).forEach((key) => {
      if (!modifiers.includes(key) || !ALL_MODIFIERS_SET.has(key)) {
        total += modifierTally[key];
      }
    });
    return total;
  }, [modifierTally, modifiers]);

  const [showModifiers, setShowModifiers] = useState(false);
  const [modifiersInput, setModifiersInput] = useState("");

  const conditions = useCodebaseStore((state) => state.conditions);

  const customProperties = useMemo(() => {
    if (!conditions) {
      return [];
    }

    if (!conditions["tailwindInstalled"]) {
      return [];
    }

    const { data, errors, met } = conditions["tailwindInstalled"];

    if (!met) {
      return [];
    }

    if (errors?.length > 0) {
      return [];
    }

    const { properties } = data;

    return properties;
  }, [conditions]);

  const [position, setPosition, positionSet, setPositionSet] = usePropertyState(
    cssValues["position"],
    true,
    PropertyType.POSITION,
    false
  );

  const positionProperties = useTailwindProperties(PropertyType.POSITION);

  const [left, setLeft, leftSet, setLeftSet] = usePropertyState(
    cssValues["left"],
    false,
    PropertyType.LEFT,
    true
  );

  const leftProperties = useTailwindProperties(PropertyType.LEFT);

  const [top, setTop, topSet, setTopSet] = usePropertyState(
    cssValues["top"],
    false,
    PropertyType.TOP,
    true
  );

  const topProperties = useTailwindProperties(PropertyType.TOP);

  const [right, setRight, rightSet, setRightSet] = usePropertyState(
    cssValues["right"],
    false,
    PropertyType.RIGHT,
    true
  );

  const rightProperties = useTailwindProperties(PropertyType.RIGHT);

  const [bottom, setBottom, bottomSet, setBottomSet] = usePropertyState(
    cssValues["bottom"],
    false,
    PropertyType.BOTTOM,
    true
  );

  const bottomProperties = useTailwindProperties(PropertyType.BOTTOM);

  const [showPositionSection, setShowPositionSection] = useState(false);

  const [width, setWidth, widthSet, setWidthSet] = usePropertyState(
    cssValues["width"],
    false,
    PropertyType.WIDTH,
    true
  );
  const widthProperties = useTailwindProperties(PropertyType.WIDTH);

  const [showMinWidth, setShowMinWidth] = useState(false);
  const [showMaxWidth, setShowMaxWidth] = useState(false);

  const [minWidth, setMinWidth, minWidthSet, setMinWidthSet] = usePropertyState(
    cssValues["min-width"],
    false,
    PropertyType.MIN_WIDTH,
    true
  );

  const minWidthProperties = useTailwindProperties(PropertyType.MIN_WIDTH);

  useEffect(() => {
    const minWidthEvaluated = cssValues["min-width"]?.evaluatedValue;
    if (minWidthEvaluated !== "auto" && minWidthSet) {
      setShowMinWidth(true);
    } else {
      setShowMinWidth(false);
    }
  }, [cssValues["min-width"]?.evaluatedValue, minWidthSet]);

  const [maxWidth, setMaxWidth, maxWidthSet, setMaxWidthSet] = usePropertyState(
    cssValues["max-width"],
    false,
    PropertyType.MAX_WIDTH,
    true
  );

  const maxWidthProperties = useTailwindProperties(PropertyType.MAX_WIDTH);

  useEffect(() => {
    const maxWidthEvaluated = cssValues["max-width"]?.evaluatedValue;
    if (maxWidthEvaluated !== "none" && maxWidthSet) {
      setShowMaxWidth(true);
    } else {
      setShowMaxWidth(false);
    }
  }, [cssValues["max-width"]?.evaluatedValue, maxWidthSet]);

  const [height, setHeight, heightSet, setHeightSet] = usePropertyState(
    cssValues["height"],
    false,
    PropertyType.HEIGHT,
    true
  );
  const heightProperties = useTailwindProperties(PropertyType.HEIGHT);

  const [showMinHeight, setShowMinHeight] = useState(false);
  const [showMaxHeight, setShowMaxHeight] = useState(false);

  const [minHeight, setMinHeight, minHeightSet, setMinHeightSet] =
    usePropertyState(
      cssValues["min-height"],
      false,
      PropertyType.MIN_HEIGHT,
      true
    );

  const minHeightProperties = useTailwindProperties(PropertyType.MIN_HEIGHT);

  useEffect(() => {
    const minHeightEvaluated = cssValues["min-height"]?.evaluatedValue;
    if (minHeightEvaluated !== "auto" && minHeightSet) {
      if (!showMinHeight) {
        setShowMinHeight(true);
      }
    } else {
      setShowMinHeight(false);
    }
  }, [cssValues["min-height"]?.evaluatedValue, minHeightSet]);

  const [maxHeight, setMaxHeight, maxHeightSet, setMaxHeightSet] =
    usePropertyState(
      cssValues["max-height"],
      false,
      PropertyType.MAX_HEIGHT,
      true
    );

  const maxHeightProperties = useTailwindProperties(PropertyType.MAX_HEIGHT);

  useEffect(() => {
    const maxHeightEvaluated = cssValues["max-height"]?.evaluatedValue;
    if (maxHeightEvaluated !== "none" && maxHeightSet) {
      if (!showMaxHeight) {
        setShowMaxHeight(true);
      }
    } else {
      setShowMaxHeight(false);
    }
  }, [cssValues["max-height"]?.evaluatedValue, maxHeightSet]);

  const [rotation, setRotation] = useState("");
  const [rotationSet, setRotationSet] = useState(
    cssValues["transform"].set || false
  );

  useEffect(() => {
    setRotationSet(cssValues["transform"].set || false);
  }, [cssValues["transform"].set]);

  useEffect(() => {
    if (leftSet || topSet || rightSet || bottomSet || positionSet) {
      setShowPositionSection(true);
    } else {
      setShowPositionSection(false);
    }
  }, [
    topSet,
    leftSet,
    rightSet,
    bottomSet,
    positionSet,
    rotationSet,
    selectedElement,
  ]);

  useEffect(() => {
    const transform = cssValues["transform"].evaluatedValue;

    let rotation;

    if (!transform || transform === "none") {
      rotation = 0;
    } else {
      const values = transform.split("(")[1].split(")")[0].split(",");
      const a = parseFloat(values[0]);
      const b = parseFloat(values[1]);

      // Calculate the angle in radians
      const radians = Math.atan2(b, a);
      // Convert the angle to degrees
      rotation = Math.round(radians * (180 / Math.PI));

      // Adjust for the range -180 to 180
      if (rotation > 180) {
        rotation -= 360;
      }
    }

    setRotation(rotation.toString());
  }, [cssValues["transform"].evaluatedValue]);

  const rotationProperties = useTailwindProperties(PropertyType.ROTATE);

  const [borderRadius, setBorderRadius] = useState("");
  const [evaluatedBorderRadius, setEvaluatedBorderRadius] = useState("");
  const [borderRadiusSet, setBorderRadiusSet] = useState(false);
  const borderRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_RADIUS
  );

  const [radiusExpanded, setRadiusExpanded] = useState(false);
  const [radiusExpandEnabled, setRadiusExpandEnabled] = useState(true);

  const [
    topLeftRadius,
    setTopLeftRadius,
    topLeftRadiusSet,
    setTopLeftRadiusSet,
  ] = usePropertyState(
    cssValues["border-top-left-radius"],
    true,
    PropertyType.BORDER_TOP_LEFT_RADIUS,
    true
  );
  const topLeftRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_LEFT_RADIUS
  );

  const [
    topRightRadius,
    setTopRightRadius,
    topRightRadiusSet,
    setTopRightRadiusSet,
  ] = usePropertyState(
    cssValues["border-top-right-radius"],
    true,
    PropertyType.BORDER_TOP_RIGHT_RADIUS,
    true
  );
  const topRightRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_RIGHT_RADIUS
  );

  const [
    bottomLeftRadius,
    setBottomLeftRadius,
    bottomLeftRadiusSet,
    setBottomLeftRadiusSet,
  ] = usePropertyState(
    cssValues["border-bottom-left-radius"],
    true,
    PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
    true
  );
  const bottomLeftRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_LEFT_RADIUS
  );

  const [
    bottomRightRadius,
    setBottomRightRadius,
    bottomRightRadiusSet,
    setBottomRightRadiusSet,
  ] = usePropertyState(
    cssValues["border-bottom-right-radius"],
    true,
    PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
    true
  );
  const bottomRightRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_RIGHT_RADIUS
  );

  const radiusIcons = [
    CornerTopLeftIcon,
    CornerTopRightIcon,
    CornerBottomRightIcon,
    CornerBottomLeftIcon,
  ];

  const [activeRadius, setActiveRadius] = useState(0);

  useEffect(() => {
    if (
      !(
        topLeftRadius === topRightRadius &&
        topRightRadius === bottomRightRadius &&
        bottomRightRadius === bottomLeftRadius &&
        topLeftRadius
      )
    ) {
      setRadiusExpandEnabled(false);
      setRadiusExpanded(true);
      setBorderRadius("");
      setEvaluatedBorderRadius("");
    } else {
      setRadiusExpandEnabled(true);
      setRadiusExpanded(false);
      setBorderRadius(topLeftRadius);
      setEvaluatedBorderRadius(
        // @ts-expect-error TS2345
        cssValues["border-top-left-radius"]?.evaluatedValue
      );
    }
  }, [topLeftRadius, topRightRadius, bottomRightRadius, bottomLeftRadius]);

  useEffect(() => {
    const borderRadiusSet =
      topLeftRadiusSet &&
      topRightRadiusSet &&
      bottomRightRadiusSet &&
      bottomLeftRadiusSet;

    setBorderRadiusSet(borderRadiusSet);
  }, [
    topLeftRadiusSet,
    topRightRadiusSet,
    bottomRightRadiusSet,
    bottomLeftRadiusSet,
  ]);

  const [parentDisplay, setParentDisplay] = useState("");
  const [parentFlexDirection, setParentFlexDirection] = useState("");

  const [darkEnabled, setDarkEnabled] = useState(false);

  useEffect(() => {
    if (parentCssEvals?.["darkEnabledInParent"]) {
      setDarkEnabled(true);
    } else {
      setDarkEnabled(false);
    }
  }, [parentCssEvals?.["darkEnabledInParent"]]);

  useEffect(() => {
    setParentDisplay(parentCssEvals?.["display"] || "");
  }, [parentCssEvals?.["display"]]);

  useEffect(() => {
    setParentFlexDirection(parentCssEvals?.["flex-direction"] || "row");
  }, [parentCssEvals?.["flex-direction"]]);

  const [flexGrow, setFlexGrow, flexGrowSet, setFlexGrowSet] = usePropertyState(
    cssValues["flex-grow"],
    false
  );

  const [flexShrink, setFlexShrink, flexShrinkSet, setFlexShrinkSet] =
    usePropertyState(cssValues["flex-shrink"], false);

  const parentIsFlex =
    parentDisplay === "flex" || parentDisplay === "inline-flex";
  const growOrShrinkSet = flexGrowSet || flexShrinkSet;

  const showFlexGrowShrinkSection = parentIsFlex || growOrShrinkSet;

  const [flexGrowShrink, setFlexGrowShrink] = useState(null);

  useEffect(() => {
    const flexGrowParsed = Number.parseInt(flexGrow);
    const flexShrinkParsed = Number.parseInt(flexShrink);

    if (flexGrowParsed === 0 && flexShrinkParsed === 0) {
      // @ts-expect-error TS2345
      setFlexGrowShrink(FlexGrowShrinkOptions.NoGrowShrink);
    } else if (flexGrowParsed > 0) {
      // @ts-expect-error TS2345
      setFlexGrowShrink(FlexGrowShrinkOptions.Grow);
    } else if (flexShrinkParsed > 0) {
      // @ts-expect-error TS2345
      setFlexGrowShrink(FlexGrowShrinkOptions.Shrink);
    } else {
      // @ts-expect-error TS2345
      setFlexGrowShrink(FlexGrowShrinkOptions.NoGrowShrink);
    }
  }, [flexGrow, flexShrink]);

  enum FlexGrowShrinkOptions {
    Grow = "Grow",
    Shrink = "Shrink",
    NoGrowShrink = "No Shrink / Grow",
  }

  const horizontalFlexGrowShrinkOptions: DropdownOptionType[] = [
    {
      label: "Grow if possible",
      value: FlexGrowShrinkOptions.Grow,
      icon: <ChevronsLeftRightIcon width={15} height={15} />,
    },
    {
      label: "Shrink if needed",
      value: FlexGrowShrinkOptions.Shrink,
      icon: <ChevronsRightLeftIcon width={15} height={15} />,
    },
    {
      label: "Don't shrink or grow",
      value: FlexGrowShrinkOptions.NoGrowShrink,
      icon: <MoveHorizontalIcon width={15} height={15} />,
    },
  ];

  const verticalFlexGrowShrinkOptions: DropdownOptionType[] = [
    {
      label: "Grow if possible",
      value: FlexGrowShrinkOptions.Grow,
      icon: (
        <ChevronsLeftRightIcon width={15} height={15} className="rotate-90" />
      ),
    },
    {
      label: "Shrink if needed",
      value: FlexGrowShrinkOptions.Shrink,
      icon: (
        <ChevronsRightLeftIcon width={15} height={15} className="rotate-90" />
      ),
    },
    {
      label: "Don't shrink or grow",
      value: FlexGrowShrinkOptions.NoGrowShrink,
      icon: <MoveHorizontalIcon width={15} height={15} className="rotate-90" />,
    },
  ];

  const handleFlexGrowShrinkDropdownSelected = (option: DropdownOptionType) => {
    const value: FlexGrowShrinkOptions = option.value;

    setFlexGrowShrink(option.value);

    switch (value) {
      case FlexGrowShrinkOptions.Grow:
        setFlexGrow("1");
        setFlexShrink("-1");
        applyCssChanges(
          {
            "flex-grow": "1",
            "flex-shrink": null,
          },
          modifiers,
          customProperties
        );
        break;
      case FlexGrowShrinkOptions.Shrink:
        setFlexShrink("1");
        setFlexGrow("-1");
        applyCssChanges(
          {
            "flex-shrink": "1",
            "flex-grow": null,
          },
          modifiers,
          customProperties
        );
        break;
      case FlexGrowShrinkOptions.NoGrowShrink:
        setFlexShrink("0");
        setFlexGrow("0");
        applyCssChanges(
          {
            "flex-shrink": "0",
            "flex-grow": "0",
          },
          modifiers,
          customProperties
        );
        break;
    }
  };

  const [showSizingSection, setShowSizingSection] = useState(false);
  const [showSizingSectionDropdown, setShowSizingSectionDropdown] =
    useState(false);

  useEffect(() => {
    if (
      widthSet ||
      heightSet ||
      minWidthSet ||
      maxWidthSet ||
      minHeightSet ||
      maxHeightSet ||
      growOrShrinkSet
    ) {
      setShowSizingSection(true);
    } else {
      setShowSizingSection(false);
    }
  }, [
    widthSet,
    heightSet,
    minWidthSet,
    maxWidthSet,
    minHeightSet,
    maxHeightSet,
    growOrShrinkSet,
    selectedElement,
  ]);

  const [
    backgroundColor,
    setBackgroundColor,
    backgroundColorSet,
    setBackgroundColorSet,
  ] = usePropertyState(
    cssValues["background-color"],
    true,
    PropertyType.BACKGROUND_COLOR,
    true
  );

  const [
    backgroundImage,
    setBackgroundImage,
    backgroundImageSet,
    setBackgroundImageSet,
  ] = usePropertyState(
    cssValues["background-image"],
    true,
    PropertyType.BACKGROUND_IMAGE,
    true
  );

  const [
    borderLeftStyle,
    setBorderLeftStyle,
    borderLeftStyleSet,
    setBorderLeftStyleSet,
  ] = usePropertyState(cssValues["border-left-style"], true);

  const [
    borderTopStyle,
    setBorderTopStyle,
    borderTopStyleSet,
    setBorderTopStyleSet,
  ] = usePropertyState(cssValues["border-top-style"], true);

  const [
    borderRightStyle,
    setBorderRightStyle,
    borderRightStyleSet,
    setBorderRightStyleSet,
  ] = usePropertyState(cssValues["border-right-style"], true);

  const [
    borderBottomStyle,
    setBorderBottomStyle,
    borderBottomStyleSet,
    setBorderBottomStyleSet,
  ] = usePropertyState(cssValues["border-bottom-style"], true);

  const [borderStyle, setBorderStyle] = useState("");
  const [evaluatedBorderStyle, setEvaluatedBorderStyle] = useState("");
  const [borderStyleSet, setBorderStyleSet] = useState(false);

  const borderStyleProperties = useTailwindProperties(
    PropertyType.BORDER_STYLE
  );

  const borderColorProperties = useTailwindProperties(
    PropertyType.BORDER_COLOR
  );

  const borderWidthProperties = useTailwindProperties(
    PropertyType.BORDER_WIDTH
  );
  const borderTopWidthProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_WIDTH
  );
  const borderRightWidthProperties = useTailwindProperties(
    PropertyType.BORDER_RIGHT_WIDTH
  );
  const borderBottomWidthProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_WIDTH
  );
  const borderLeftWidthProperties = useTailwindProperties(
    PropertyType.BORDER_LEFT_WIDTH
  );

  useEffect(() => {
    if (
      !(
        borderLeftStyle === borderTopStyle &&
        borderTopStyle === borderRightStyle &&
        borderRightStyle === borderBottomStyle &&
        borderLeftStyle
      )
    ) {
      setBorderStyle("");
      setEvaluatedBorderStyle("");
      setBorderStyleSet(false);
    } else {
      setBorderStyle(borderLeftStyle);
      // @ts-expect-error TS2345
      setEvaluatedBorderStyle(cssValues["border-left-style"]?.evaluatedValue);
      setBorderStyleSet(true);
    }
  }, [borderLeftStyle, borderTopStyle, borderRightStyle, borderBottomStyle]);

  const [
    borderLeftWidth,
    setBorderLeftWidth,
    borderLeftWidthSet,
    setBorderLeftWidthSet,
  ] = usePropertyState(cssValues["border-left-width"], false);

  const [
    borderTopWidth,
    setBorderTopWidth,
    borderTopWidthSet,
    setBorderTopWidthSet,
  ] = usePropertyState(cssValues["border-top-width"], false);

  const [
    borderRightWidth,
    setBorderRightWidth,
    borderRightWidthSet,
    setBorderRightWidthSet,
  ] = usePropertyState(cssValues["border-right-width"], false);

  const [
    borderBottomWidth,
    setBorderBottomWidth,
    borderBottomWidthSet,
    setBorderBottomWidthSet,
  ] = usePropertyState(cssValues["border-bottom-width"], false);

  const [borderWidth, setBorderWidth] = useState("");
  const [evaluatedBorderWidth, setEvaluatedBorderWidth] = useState("");
  const [borderWidthSet, setBorderWidthSet] = useState(false);

  const [borderWidthExpanded, setBorderWidthExpanded] = useState(false);

  const BORDER_WIDTH_OPTIONS: DiscreteOption[] = [
    {
      key: "all",
      option: {
        css: "",
        tailwind: "",
      },
      labelContent: <BorderAllIcon />,
      tooltipContent: "All",
    },
    {
      key: "individual",
      option: {
        css: "",
        tailwind: "",
      },
      labelContent: <BorderSplitIcon />,
      tooltipContent: "Individual",
    },
  ];

  const [borderSet, setBorderSet] = useState(false);

  const parseWidth = (widthStr: string): number => {
    return parseInt(widthStr, 10); // Parse "10px" to 10, for example
  };

  useEffect(() => {
    const borderRightWidthEvaluated =
      cssValues["border-right-width"]?.evaluatedValue;
    const borderLeftWidthEvaluated =
      cssValues["border-left-width"]?.evaluatedValue;
    const borderTopWidthEvaluated =
      cssValues["border-top-width"]?.evaluatedValue;
    const borderBottomWidthEvaluated =
      cssValues["border-bottom-width"]?.evaluatedValue;

    const evaluatedWidthEqual =
      borderLeftWidthEvaluated === borderTopWidthEvaluated &&
      borderTopWidthEvaluated === borderRightWidthEvaluated &&
      borderRightWidthEvaluated === borderBottomWidthEvaluated;

    // @ts-expect-error TS2345
    const parsedTopWidth = parseWidth(borderTopWidthEvaluated);
    // @ts-expect-error TS2345
    const parsedRightWidth = parseWidth(borderRightWidthEvaluated);
    // @ts-expect-error TS2345
    const parsedBottomWidth = parseWidth(borderRightWidthEvaluated);
    // @ts-expect-error TS2345
    const parsedLeftWidth = parseWidth(borderLeftWidthEvaluated);

    const nonEmptyWidth =
      parsedTopWidth > 0 ||
      parsedRightWidth > 0 ||
      parsedBottomWidth > 0 ||
      parsedLeftWidth > 0;

    if (nonEmptyWidth) {
      setBorderSet(true);
    } else {
      setBorderSet(false);
    }

    if (!evaluatedWidthEqual) {
      setBorderWidthExpanded(true);
      setBorderWidth("");
      setEvaluatedBorderWidth("");
      setBorderWidthSet(false);
    } else {
      setBorderWidthExpanded(false);
      // @ts-expect-error TS2345
      setBorderWidth(borderLeftWidthEvaluated);
      // @ts-expect-error TS2345
      setEvaluatedBorderWidth(cssValues["border-left-width"]?.evaluatedValue);
      setBorderWidthSet(true);
    }
  }, [
    cssValues["border-right-width"]?.evaluatedValue,
    cssValues["border-left-width"]?.evaluatedValue,
    cssValues["border-top-width"]?.evaluatedValue,
    cssValues["border-bottom-width"]?.evaluatedValue,
  ]);

  const [borderLeftColor] = usePropertyState(
    cssValues["border-left-color"],
    false
  );

  const [borderTopColor] = usePropertyState(
    cssValues["border-top-color"],
    false
  );

  const [borderRightColor] = usePropertyState(
    cssValues["border-right-color"],
    false
  );

  const [borderBottomColor] = usePropertyState(
    cssValues["border-bottom-color"],
    false
  );

  const [borderColor, setBorderColor] = useState("");
  const [evaluatedBorderColor, setEvaluatedBorderColor] = useState("");
  const [borderColorSet, setBorderColorSet] = useState(false);

  useEffect(() => {
    if (
      !(
        borderLeftColor === borderTopColor &&
        borderTopColor === borderRightColor &&
        borderRightColor === borderBottomColor &&
        borderLeftColor !== ""
      )
    ) {
      setBorderColor("");
      setEvaluatedBorderColor("");
      setBorderColorSet(false);
    } else {
      setBorderColorSet(true);
      // @ts-expect-error TS2345
      setEvaluatedBorderColor(cssValues["border-left-color"].evaluatedValue);
      setBorderColor(borderLeftColor);
    }
  }, [borderLeftColor, borderTopColor, borderRightColor, borderBottomColor]);

  const [
    textDecorationLine,
    setTextDecorationLine,
    textDecorationLineSet,
    setTextDecorationLineSet,
  ] = usePropertyState(
    cssValues["text-decoration-line"],
    false,
    PropertyType.TEXT_DECORATION_LINE,
    true
  );

  const TEXT_DECORATION_LINE_OPTIONS: DiscreteOption[] = [
    {
      key: "underline",
      option: {
        tailwind: "underline",
        css: "text-decoration-line: underline",
      },
      labelContent: <UnderlineIcon />,
      tooltipContent: "Underline",
    },
    {
      key: "line-through",
      option: {
        tailwind: "line-through",
        css: "text-decoration-line: line-through",
      },
      labelContent: <StrikethroughIcon />,
      tooltipContent: "Line Through",
    },
    {
      key: "overline",
      option: {
        tailwind: "overline",
        css: "text-decoration-line: overline",
      },
      labelContent: <OverlineIcon />,
      tooltipContent: "Overline",
    },
  ];

  const [fontStyle, setFontStyle, fontStyleSet, setFontStyleSet] =
    usePropertyState(
      cssValues["font-style"],
      false,
      PropertyType.FONT_STYLE,
      true
    );

  const [fontFamily, setFontFamily, fontFamilySet, setFontFamilySet] =
    usePropertyState(
      cssValues["font-family"],
      true,
      PropertyType.FONT_FAMILY,
      true
    );

  let fontFamilyDecorated = "Default";

  if (fontFamily.startsWith("ui-serif")) {
    fontFamilyDecorated = "Serif";
  } else if (fontFamily.startsWith("ui-sans")) {
    fontFamilyDecorated = "Sans";
  } else if (fontFamily.startsWith("ui-mono")) {
    fontFamilyDecorated = "Mono";
  }

  const fontFamilyProperties = useTailwindProperties(PropertyType.FONT_FAMILY);
  const [fontWeight, setFontWeight, fontWeightSet, setFontWeightSet] =
    usePropertyState(
      cssValues["font-weight"],
      false,
      PropertyType.FONT_WEIGHT,
      true
    );
  const fontWeightProperties = useTailwindProperties(PropertyType.FONT_WEIGHT);

  const [fontSize, setFontSize, fontSizeSet, setFontSizeSet] = usePropertyState(
    cssValues["font-size"],
    false,
    PropertyType.FONT_SIZE,
    true
  );
  const fontSizeProperties = useTailwindProperties(PropertyType.FONT_SIZE);

  const [lineHeight, setLineHeight, lineHeightSet, setLineHeightSet] =
    usePropertyState(
      cssValues["line-height"],
      false,
      PropertyType.LINE_HEIGHT,
      true
    );
  const lineHeightProperties = useTailwindProperties(PropertyType.LINE_HEIGHT);

  const [
    letterSpacing,
    setLetterSpacing,
    letterSpacingSet,
    setLetterSpacingSet,
  ] = usePropertyState(
    cssValues["letter-spacing"],
    false,
    PropertyType.LETTER_SPACING,
    true
  );
  const letterSpacingProperties = useTailwindProperties(
    PropertyType.LETTER_SPACING
  );

  const [textColor, setTextColor, textColorSet, setTextColorSet] =
    usePropertyState(cssValues["color"], true, PropertyType.TEXT_COLOR, true);

  const [textAlign, setTextAlign, textAlignSet, setTextAlignSet] =
    usePropertyState(cssValues["text-align"], true);

  const textColorProperties = useTailwindProperties(PropertyType.TEXT_COLOR);

  const TEXT_ALIGN_OPTIONS: DiscreteOption[] = [
    {
      key: "left",
      option: {
        tailwind: "text-left",
        css: "text-align: left",
      },
      labelContent: <TextAlignLeftIcon />,
      tooltipContent: "Text align left",
    },
    {
      key: "center",
      option: {
        tailwind: "text-center",
        css: "text-align: center",
      },
      labelContent: <TextAlignCenterIcon />,
      tooltipContent: "Text align center",
    },
    {
      key: "right",
      option: {
        tailwind: "text-right",
        css: "text-align: right",
      },
      labelContent: <TextAlignRightIcon />,
      tooltipContent: "Text align right",
    },
    {
      key: "justify",
      option: {
        tailwind: "text-justify",
        css: "text-align: justify",
      },
      labelContent: <TextAlignJustifyIcon />,
      tooltipContent: "Text align justified",
    },
  ];

  const [showTextSection, setShowTextSection] = useState(false);

  useEffect(() => {
    const textPropertiesSet =
      fontWeightSet ||
      lineHeightSet ||
      fontSizeSet ||
      letterSpacingSet ||
      textColorSet ||
      textAlignSet;
    setShowTextSection(textPropertiesSet);
  }, [
    selectedElement,
    fontWeightSet,
    lineHeightSet,
    fontSizeSet,
    letterSpacingSet,
    textColorSet,
    textAlignSet,
  ]);

  const [paddingTop, setPaddingTop, paddingTopSet, setPaddingTopSet] =
    usePropertyState(
      cssValues["padding-top"],
      false,
      PropertyType.PADDING_TOP,
      true
    );
  const paddingTopProperties = useTailwindProperties(PropertyType.PADDING_TOP);

  const [
    paddingBottom,
    setPaddingBottom,
    paddingBottomSet,
    setPaddingBottomSet,
  ] = usePropertyState(
    cssValues["padding-bottom"],
    false,
    PropertyType.PADDING_BOTTOM,
    true
  );
  const paddingBottomProperties = useTailwindProperties(
    PropertyType.PADDING_BOTTOM
  );

  const [paddingLeft, setPaddingLeft, paddingLeftSet, setPaddingLeftSet] =
    usePropertyState(
      cssValues["padding-left"],
      false,
      PropertyType.PADDING_LEFT,
      true
    );

  const paddingLeftProperties = useTailwindProperties(
    PropertyType.PADDING_LEFT
  );

  const [paddingRight, setPaddingRight, paddingRightSet, setPaddingRightSet] =
    usePropertyState(
      cssValues["padding-right"],
      false,
      PropertyType.PADDING_RIGHT,
      true
    );
  const paddingRightProperties = useTailwindProperties(
    PropertyType.PADDING_RIGHT
  );

  const [paddingVertical, setPaddingVertical] = useState("");
  const [evaluatedPaddingVertical, setEvaluatedPaddingVertical] = useState("");
  const [paddingVerticalSet, setPaddingVerticalSet] = useState(false);
  const paddingVerticalProperties = useTailwindProperties(
    PropertyType.PADDING_VERTICAL
  );

  const [paddingHorizontal, setPaddingHorizontal] = useState("");
  const [evaluatedPaddingHorizontal, setEvaluatedPaddingHorizontal] =
    useState("");
  const [paddingHorizontalSet, setPaddingHorizontalSet] = useState(false);
  const paddingHorizontalProperties = useTailwindProperties(
    PropertyType.PADDING_HORIZONTAL
  );

  useEffect(() => {
    if (paddingBottom == paddingTop && paddingTop) {
      setPaddingVertical(paddingTop);
      // @ts-expect-error TS2345
      setEvaluatedPaddingVertical(cssValues["padding-top"]?.evaluatedValue);
    } else {
      setPaddingVertical("");
      setEvaluatedPaddingVertical("");
    }
  }, [paddingTop, paddingBottom]);

  useEffect(() => {
    if (paddingLeft == paddingRight && paddingLeft) {
      setPaddingHorizontal(paddingLeft);
      // @ts-expect-error TS2345
      setEvaluatedPaddingHorizontal(cssValues["padding-left"]?.evaluatedValue);
    } else {
      setPaddingHorizontal("");
      setEvaluatedPaddingHorizontal("");
    }
  }, [paddingLeft, paddingRight]);

  const [paddingExpanded, setPaddingExpanded] = useState(false);
  const [paddingExpandEnabled, setPaddingExpandEnabled] = useState(true);

  useEffect(() => {
    if (paddingBottom !== paddingTop || paddingLeft !== paddingRight) {
      setPaddingExpanded(true);
      setPaddingExpandEnabled(false);
    } else {
      setPaddingExpanded(false);
      setPaddingExpandEnabled(true);
    }
  }, [paddingBottom, paddingTop, paddingLeft, paddingRight]);

  useEffect(() => {
    setPaddingVerticalSet(paddingTopSet && paddingBottomSet);
  }, [paddingTopSet, paddingBottomSet]);

  useEffect(() => {
    setPaddingHorizontalSet(paddingLeftSet && paddingRightSet);
  }, [paddingLeftSet, paddingRightSet]);

  const [marginTop, setMarginTop, marginTopSet, setMarginTopSet] =
    usePropertyState(
      cssValues["margin-top"],
      false,
      PropertyType.MARGIN_TOP,
      true
    );
  const marginTopProperties = useTailwindProperties(PropertyType.MARGIN_TOP);

  const [marginBottom, setMarginBottom, marginBottomSet, setMarginBottomSet] =
    usePropertyState(
      cssValues["margin-bottom"],
      false,
      PropertyType.MARGIN_BOTTOM,
      true
    );
  const marginBottomProperties = useTailwindProperties(
    PropertyType.MARGIN_BOTTOM
  );

  const [marginLeft, setMarginLeft, marginLeftSet, setMarginLeftSet] =
    usePropertyState(cssValues["margin-left"], false, PropertyType.MARGIN_LEFT);
  const marginLeftProperties = useTailwindProperties(PropertyType.MARGIN_LEFT);

  const [marginRight, setMarginRight, marginRightSet, setMarginRightSet] =
    usePropertyState(
      cssValues["margin-right"],
      false,
      PropertyType.MARGIN_RIGHT
    );
  const marginRightProperties = useTailwindProperties(
    PropertyType.MARGIN_RIGHT
  );

  const [marginVertical, setMarginVertical] = useState("");
  const [evaluatedMarginVertical, setEvaluatedMarginVertical] = useState("");
  const [marginVerticalSet, setMarginVerticalSet] = useState(false);
  const marginVerticalProperties = useTailwindProperties(
    PropertyType.MARGIN_VERTICAL
  );

  const [marginHorizontal, setMarginHorizontal] = useState("");
  const [evaluatedMarginHorizontal, setEvaluatedMarginHorizontal] =
    useState("");
  const [marginHorizontalSet, setMarginHorizontalSet] = useState(false);
  const marginHorizontalProperties = useTailwindProperties(
    PropertyType.MARGIN_HORIZONTAL
  );

  useEffect(() => {
    if (marginBottom == marginTop && marginTop) {
      setMarginVertical(marginTop);
      // @ts-expect-error TS2345
      setEvaluatedMarginVertical(cssValues["margin-top"]?.evaluatedValue);
    } else {
      setMarginVertical("");
      setEvaluatedMarginVertical("");
    }
  }, [marginTop, marginBottom]);

  useEffect(() => {
    if (marginLeft == marginRight && marginLeft) {
      setMarginHorizontal(marginLeft);
      // @ts-expect-error TS2345
      setEvaluatedMarginHorizontal(cssValues["margin-left"]?.evaluatedValue);
    } else {
      setMarginHorizontal("");
      setEvaluatedMarginHorizontal("");
    }
  }, [marginLeft, marginRight]);

  const [marginExpanded, setMarginExpanded] = useState(false);
  const [marginExpandEnabled, setMarginExpandEnabled] = useState(true);

  useEffect(() => {
    if (marginBottom !== marginTop || marginLeft !== marginRight) {
      setMarginExpanded(true);
      setMarginExpandEnabled(false);
    } else {
      setMarginExpanded(false);
      setMarginExpandEnabled(true);
    }
  }, [marginBottom, marginTop, marginLeft, marginRight]);

  useEffect(() => {
    setMarginVerticalSet(marginTopSet && marginBottomSet);
  }, [marginTopSet, marginBottomSet]);

  useEffect(() => {
    setMarginHorizontalSet(marginLeftSet && marginRightSet);
  }, [marginLeftSet, marginRightSet]);

  const [showSpacingSection, setShowSpacingSection] = useState(false);

  useEffect(() => {
    if (
      paddingBottomSet ||
      paddingTopSet ||
      paddingLeftSet ||
      paddingRightSet ||
      marginTopSet ||
      marginBottomSet ||
      marginLeftSet ||
      marginRightSet
    ) {
      setShowSpacingSection(true);
    } else {
      setShowSpacingSection(false);
    }
  }, [
    paddingBottomSet,
    paddingTopSet,
    paddingLeftSet,
    paddingRightSet,
    marginTopSet,
    marginBottomSet,
    marginLeftSet,
    marginRightSet,
    selectedElement,
  ]);

  const transformToApplyCssChangesFormat = (cssString) => {
    // Remove comments
    const noComments = cssString.replace(/\/\*.*?\*\//g, "");

    // Split into rules and extract properties/values
    const rules = noComments.split(";").filter((rule) => rule.trim() !== "");
    return rules.reduce((acc, rule) => {
      const [property, value] = rule.split(":").map((part) => part.trim());
      if (property && value) {
        acc[property] = value;
      }
      return acc;
    }, {});
  };

  const [display, setDisplay] = usePropertyState(cssValues["display"], true);
  const [flexDirection, setFlexDirection, flexDirectionSet] = usePropertyState(
    cssValues["flex-direction"],
    false
  );

  const FLEX_DIRECTION_OPTIONS: DiscreteOption[] = [
    {
      key: "column",
      option: {
        tailwind: "flex-col",
        css: "flex-direction: column",
      },
      labelContent: <ArrowDownIcon width={15} height={15} />,
      tooltipContent: "Vertical layout",
    },
    {
      key: "column-reverse",
      option: {
        tailwind: "flex-col-reverse",
        css: "flex-direction: column-reverse",
      },
      labelContent: <ArrowUpIcon width={15} height={15} />,
      tooltipContent: "Vertical layout reversed",
    },
    {
      key: "row",
      option: {
        tailwind: "flex-row",
        css: "flex-direction: row",
      },
      labelContent: <ArrowRightIcon width={15} height={15} />,
      tooltipContent: "Horizontal layout",
    },
    {
      key: "row-reverse",
      option: {
        tailwind: "flex-row-reverse",
        css: "flex-direction: row-reverse",
      },
      tooltipContent: "Horizontal layout reversed",
      labelContent: <ArrowLeftIcon width={15} height={15} />,
    },
  ];

  const [flexWrap, setFlexWrap, flexWrapSet] = usePropertyState(
    cssValues["flex-wrap"],
    false
  );

  const FLEX_WRAP_OPTIONS: DiscreteOption[] = [
    {
      key: "nowrap",
      option: {
        tailwind: "flex-nowrap",
        css: "flex-wrap: nowrap",
      },
      labelContent: <NoWrapIcon width={16} height={16} strokeWidth={3} />,
      tooltipContent: "No wrap",
    },
    {
      key: "wrap",
      option: {
        tailwind: "flex-wrap",
        css: "flex-wrap: wrap",
      },
      labelContent: <WrapIcon width={16} height={16} />,
      tooltipContent: "Wrap",
    },
    {
      key: "wrap-reverse",
      option: {
        tailwind: "flex-wrap-reverse",
        css: "flex-wrap: wrap-reverse",
      },
      labelContent: <WrapReverseIcon width={16} height={16} />,
      tooltipContent: "Wrap reversed",
    },
  ];

  const [alignContent, setAlignContent, alignContentSet] = usePropertyState(
    cssValues["align-content"],
    false
  );

  const ALIGN_CONTENT_OPTIONS: DiscreteOption[] = useMemo(() => {
    let props: { width: number; height: number; className?: string } = {
      width: 20,
      height: 20,
    };
    return [
      {
        key: "flex-start",
        option: {
          tailwind: "content-start",
          css: "align-content: flex-start;",
        },
        labelContent: <AlignContentFlexStartIcon {...props} />,
        tooltipContent: "Align content start",
      },
      {
        key: "center",
        option: {
          tailwind: "content-center",
          css: "align-content: center;",
        },
        labelContent: <AlignContentCenterIcon {...props} />,
        tooltipContent: "Align content center",
      },
      {
        key: "flex-end",
        option: {
          tailwind: "content-end",
          css: "align-content: flex-end;",
        },
        labelContent: <AlignContentFlexEndIcon {...props} />,
        tooltipContent: "Align content end",
      },
      {
        key: "stretch",
        option: {
          tailwind: "content-stretch",
          css: "align-content: stretch;",
        },
        labelContent: (
          <AlignContentStretchIcon
            style={{
              width: 18,
              height: 18,
            }}
          />
        ),
        tooltipContent: "Align content stretch",
      },
      {
        key: "space-between",
        option: {
          tailwind: "content-between",
          css: "align-content: space-between;",
        },
        labelContent: (
          <AlignContentSpaceBetweenIcon
            style={{
              width: 18,
              height: 18,
            }}
          />
        ),
        tooltipContent: "Align content space between",
      },
      {
        key: "space-around",
        option: {
          tailwind: "content-around",
          css: "align-content: space-around;",
        },
        labelContent: <AlignContentSpaceAroundIcon {...props} />,
        tooltipContent: "Align content space around",
      },
    ];
  }, []);

  const ALIGN_ITEM_OPTIONS: DiscreteOption[] = useMemo(() => {
    let props: {
      width: number;
      height: number;
      className?: string;
      fill?: string;
    } = {
      width: 18,
      height: 18,
      fill: "currentColor",
    };

    if (flexDirection?.startsWith("row")) {
      props = {
        ...props,
        className: "rotate-90",
      };
    }

    return [
      {
        key: "flex-start",
        option: {
          tailwind: "items-start",
          css: "align-items: flex-start;",
        },
        labelContent: <AlignItemsStartIcon {...props} />,
        tooltipContent: "Align items start",
      },
      {
        key: "center",
        option: {
          tailwind: "items-center",
          css: "align-items: center;",
        },
        labelContent: <AlignItemsCenterIcon {...props} />,
        tooltipContent: "Align items center",
      },
      {
        key: "flex-end",
        option: {
          tailwind: "items-end",
          css: "align-items: flex-end;",
        },
        labelContent: <AlignItemsEndIcon {...props} />,
        tooltipContent: "Align items end",
      },
      {
        key: "stretch",
        option: {
          tailwind: "items-stretch",
          css: "align-items: stretch;",
        },
        labelContent: <AlignItemsStretchIcon {...props} />,
        tooltipContent: "Align items stretch",
      },
      {
        key: "baseline",
        option: {
          tailwind: "items-baseline",
          css: "align-items: baseline;",
        },
        labelContent: <AlignItemsBaseline {...props} />,
        tooltipContent: "Align items baseline",
      },
    ];
  }, [flexDirection]);

  const JUSTIFY_CONTENT_OPTIONS: DiscreteOption[] = useMemo(() => {
    let props: { width: number; height: number; className?: string } = {
      width: 18,
      height: 18,
    };

    if (flexDirection?.startsWith("column")) {
      props = {
        ...props,
        className: "rotate-90",
      };
    }
    return [
      {
        key: "flex-start",
        option: {
          tailwind: "justify-start",
          css: "justify-content: flex-start;",
        },
        labelContent: <JustifyFlexStartIcon {...props} />,
        tooltipContent: "Justify content start",
      },
      {
        key: "center",
        option: {
          tailwind: "justify-center",
          css: "justify-content: center;",
        },
        labelContent: <JustifyCenterIcon {...props} />,
        tooltipContent: "Justify content center",
      },
      {
        key: "flex-end",
        option: {
          tailwind: "justify-end",
          css: "justify-content: flex-end;",
        },
        labelContent: <JustifyFlexEndIcon {...props} />,
        tooltipContent: "Justify content end",
      },
      {
        key: "space-between",
        option: {
          tailwind: "justify-between",
          css: "justify-content: space-between;",
        },
        labelContent: <JustifySpaceBetweenIcon {...props} />,
        tooltipContent: "Justify content between",
      },
      {
        key: "space-evenly",
        option: {
          tailwind: "justify-evenly",
          css: "justify-content: space-evenly;",
        },
        labelContent: <JustifySpaceEvenlyIcon {...props} />,
        tooltipContent: "Justify content evenly",
      },
    ];
  }, [flexDirection]);

  const [rowGap, setRowGap, rowGapSet, setRowGapSet] = usePropertyState(
    cssValues["row-gap"],
    true,
    PropertyType.ROW_GAP,
    true
  );

  const [justifyContent, setJustifyContent] = usePropertyState(
    cssValues["justify-content"],
    false
  );

  const [alignItems, setAlignItems] = usePropertyState(
    cssValues["align-items"],
    false
  );

  const rowGapProperties = useTailwindProperties(PropertyType.ROW_GAP);

  const [columnGap, setColumnGap, columnGapSet, setColumnGapSet] =
    usePropertyState(
      cssValues["column-gap"],
      true,
      PropertyType.COLUMN_GAP,
      true
    );

  const columnGapProperties = useTailwindProperties(PropertyType.COLUMN_GAP);

  const [layoutIndex, setLayoutIndex] = useState(0);

  const addPadding = (recentOption: ComboBoxOptionType) => {
    const recentOptionObject = transformToApplyCssChangesFormat(
      recentOption.css
    );

    addPaddingWithStyle(recentOptionObject);
  };

  const addPaddingWithStyle = (recentOptionObject: any) => {
    const changes = {
      "padding-left": paddingLeftSet ? paddingLeft : null,
      "padding-right": paddingRightSet ? paddingRight : null,
      "padding-top": paddingTopSet ? paddingTop : null,
      "padding-bottom": paddingBottomSet ? paddingBottom : null,
      ...recentOptionObject,
    };

    applyCssChanges(changes, modifiers, customProperties);
  };

  const addMargin = (recentOption: ComboBoxOptionType) => {
    const recentOptionObject = transformToApplyCssChangesFormat(
      recentOption.css
    );

    addMarginWithStyle(recentOptionObject);
  };

  const addMarginWithStyle = (recentOptionObject: any) => {
    const changes = {
      "margin-left": marginLeftSet ? marginLeft : null,
      "margin-right": marginRightSet ? marginRight : null,
      "margin-top": marginTopSet ? marginTop : null,
      "margin-bottom": marginBottomSet ? marginBottom : null,
      ...recentOptionObject,
    };

    applyCssChanges(changes, modifiers, customProperties);
  };

  const changeBorderWidth = (obj) => {
    const useGlobalWidth = obj.hasOwnProperty("border-width");

    let changes = {};

    if (useGlobalWidth) {
      changes = {
        "border-left-width": obj["border-width"],
        "border-right-width": obj["border-width"],
        "border-top-width": obj["border-width"],
        "border-bottom-width": obj["border-width"],
      };
    } else {
      changes = {
        "border-left-width": obj["border-left-width"] || borderLeftWidth,
        "border-right-width": obj["border-right-width"] || borderRightWidth,
        "border-top-width": obj["border-top-width"] || borderTopWidth,
        "border-bottom-width": obj["border-bottom-width"] || borderBottomWidth,
      };
    }

    applyCssChanges(changes, modifiers, customProperties);
  };

  const removeStyles = useCallback(
    (...keys) => {
      const changes = keys.reduce((acc, key) => {
        acc[key] = DELETE_STYLE_CONSTANT;
        return acc;
      }, {});

      applyCssChanges(changes, modifiers, customProperties);
    },
    [applyCssChanges, modifiers, customProperties]
  );

  const justifyContentMap = ["flex-start", "center", "flex-end"];
  const alignItemsMap = ["flex-start", "center", "flex-end"];

  const modifyForReverse = (direction, alignment) => {
    if (direction?.endsWith("reverse")) {
      const reverseMap = {
        "flex-start": "flex-end",
        "flex-end": "flex-start",
        center: "center",
      };
      return reverseMap[alignment];
    }
    return alignment;
  };

  const getIndexFromStyles = (direction, justifyContent, alignItems) => {
    justifyContent = modifyForReverse(direction, justifyContent);

    const justifyContentIndex = justifyContentMap.indexOf(justifyContent);
    const alignItemsIndex = alignItemsMap.indexOf(alignItems);

    if (justifyContentIndex === -1 || alignItemsIndex === -1) {
      return -1;
    }

    let index;
    if (!direction || direction.startsWith("row")) {
      index = alignItemsIndex * 3 + justifyContentIndex;
    } else {
      index = justifyContentIndex * 3 + alignItemsIndex;
    }

    return index;
  };

  useEffect(() => {
    const index = getIndexFromStyles(flexDirection, justifyContent, alignItems);
    setLayoutIndex(index);
  }, [flexDirection, justifyContent, alignItems]);

  const getStylesFromIndex = (direction, index) => {
    let justifyContent, alignItems;

    if (direction.startsWith("row")) {
      justifyContent = justifyContentMap[index % 3];
      alignItems = alignItemsMap[Math.floor(index / 3)];
    } else {
      justifyContent = justifyContentMap[Math.floor(index / 3)];
      alignItems = alignItemsMap[index % 3];
    }

    justifyContent = modifyForReverse(direction, justifyContent);

    return { justifyContent, alignItems };
  };

  const addLayout = (index: number) => {
    const styles = getStylesFromIndex(flexDirection, index);

    const changes = {
      "justify-content": styles.justifyContent,
      "align-items": styles.alignItems,
    };

    applyCssChanges(changes, modifiers, customProperties);
  };

  const addTailwindClass = useCallback(
    ({ tailwind: className, css: cssEquivalent }: ComboBoxOptionType) => {
      const addClassChange = new AddClassChange({
        codebaseIdToAddClass: selectedElement.codebaseId,
        className: className,
        addingTailwindClass: true,
        cssEquivalent,
        modifiers,
        customProperties,
      });

      addChange(addClassChange);
      dispatch(setIFrameChanged());
      setRecentClassesCache((prev) => ({
        ...prev,
        [className]: true,
      }));
    },
    [selectedElement.codebaseId, modifiers]
  );

  const addTailwindClassInstantOnly = useCallback(
    _.throttle(
      ({ tailwind: className, css: cssEquivalent }: ComboBoxOptionType) => {
        const addClassChange = new AddClassChange({
          codebaseIdToAddClass: selectedElement.codebaseId,
          className: "",
          modifiers,
          addingTailwindClass: true,
          cssEquivalent,
          temporaryOnly: true,
          customProperties,
        });

        dispatch(
          sendMessageToIFrame({
            type: "executeFunction",
            storyboardId: selectedElement.storyboardId,
            payload: {
              functionName: "applyChangeItemToDocument",
              args: [addClassChange],
            },
          })
        );
      },
      25,
      {
        leading: true,
        trailing: false,
      }
    ),
    [selectedElement.codebaseId, modifiers]
  );

  const removeTailwindClass = useCallback(
    (className: string) => {
      addChange(
        new RemoveClassChange({
          codebaseIdToRemoveClass: selectedElement.codebaseId,
          className,
        })
      );

      setRecentClassesCache((prev) => ({
        ...prev,
        [className]: false,
      }));
    },
    [selectedElement]
  );

  const buildTextColorPreview = (option: ComboBoxOptionType): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return (
      <div className={`w-full h-full font-bold`} style={styles}>
        Aa
      </div>
    );
  };

  const buildFontWeightColorPreview = (
    option: ComboBoxOptionType
  ): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return (
      <div className={`w-full h-full`} style={styles}>
        Tt
      </div>
    );
  };

  type EffectOption = {
    properties: ClassType[];
    type: string;
    exclusive: boolean;
    icon: ReactNode;
  };

  type EffectClass = {
    value: string;
  };

  type Effect = EffectOption & EffectClass;

  const effectOptions: EffectOption[] = [
    {
      properties: useTailwindProperties(PropertyType.ANIMATION),
      type: "Animation",
      exclusive: true,
      icon: <MagicWandIcon />,
    },
    {
      properties: useTailwindProperties(PropertyType.BOX_SHADOW),
      type: "Shadow",
      exclusive: true,
      icon: <ShadowIcon />,
    },
    {
      properties: useTailwindProperties(PropertyType.BLUR),
      type: "Blur",
      exclusive: true,
      icon: <BlendingModeIcon />,
    },
  ];

  const [effects, setEffects] = useState<Effect[] | null>([]);

  const setSortedEffects = (newEffects) => {
    const sortedEffects = newEffects.sort((a, b) =>
      a.type.localeCompare(b.type)
    );
    setEffects(sortedEffects);
  };

  useEffect(() => {
    if (classListWithCache) {
      const newEffects: Effect[] = classListWithCache
        .map((className) => {
          // Find the corresponding effect for each class
          const effect = effectOptions.find((e) =>
            e.properties
              .map((property) => property.tailwind)
              .includes(className)
          );
          return effect ? { value: className, ...effect } : null;
        })
        .filter((effect) => effect !== null);

      setSortedEffects(newEffects);
    } else {
      setSortedEffects([]);
    }
  }, [classListWithCache]);

  const effectDropdownOptions = useCallback(() => {
    return effectOptions
      .filter(
        (effect) =>
          // @ts-expect-error TS18047
          !effect.exclusive || !effects.some((e) => e.type === effect.type)
      )
      .map((effect) => ({
        label: effect.type,
        value: effect.type,
        icon: effect.icon,
      }));
  }, [effects, effectOptions]);

  const buildEffectFromOption = (effectOption: EffectOption): Effect => {
    return { ...effectOption, value: "" };
  };

  const addEffect = () => {
    const effectOptionToAdd = effectOptions.find(
      (effect) =>
        // @ts-expect-error TS18047
        !effect.exclusive || !effects.some((e) => e.type === effect.type)
    );

    if (effectOptionToAdd) {
      // @ts-expect-error TS2488
      setSortedEffects([...effects, buildEffectFromOption(effectOptionToAdd)]);
    }
  };

  const removeEffect = (indexToRemove) => {
    // @ts-expect-error TS18047
    setSortedEffects(effects.filter((_, index) => index !== indexToRemove));
  };

  const onEffectDropdownSelected = (
    option: DropdownOptionType,
    effectIndex
  ) => {
    const selectedEffectOption = effectOptions.find(
      (effect) => effect.type === option.label
    );

    if (selectedEffectOption) {
      setSortedEffects(
        // @ts-expect-error TS18047
        effects.map((effect, index) => {
          if (index === effectIndex) {
            return buildEffectFromOption(selectedEffectOption);
          }
          return effect;
        })
      );
    }
  };

  const widthDropdownOptions: DropdownOptionType[] = useMemo(() => {
    let options = [
      {
        label: "Add min width...",
        value: "minWidth",
        icon: <MinWidthIcon />,
      },
      {
        label: "Add max width...",
        value: "maxWidth",
        icon: <MaxWidthIcon />,
      },
    ];

    // Filter options based on whether minWidth and maxWidth are set
    options = options.filter((option) => {
      if (option.value === "minWidth" && showMinWidth) {
        return false; // Exclude the min width option if it's already set
      }
      if (option.value === "maxWidth" && showMaxWidth) {
        return false; // Exclude the max width option if it's already set
      }
      return true;
    });

    return options;
  }, [showMaxWidth, showMinWidth]);

  const handleWidthDropdownSelected = (option: DropdownOptionType) => {
    switch (option.value) {
      case "minWidth":
        setShowMinWidth(true);
        break;
      case "maxWidth":
        setShowMaxWidth(true);
        break;
      default:
        console.error("width option not recognized");
        break;
    }
  };

  const heightDropdownOptions: DropdownOptionType[] = useMemo(() => {
    let options = [
      {
        label: "Add min height...",
        value: "minHeight",
        icon: <MinWidthIcon className="rotate-90" />,
      },
      {
        label: "Add max height...",
        value: "maxHeight",
        icon: <MaxWidthIcon className="rotate-90" />,
      },
    ];

    options = options.filter((option) => {
      if (option.value === "minHeight" && showMinHeight) {
        return false;
      }
      if (option.value === "maxHeight" && showMaxHeight) {
        return false;
      }
      return true;
    });

    return options;
  }, [showMaxHeight, showMinHeight]);

  const handleHeightDropdownSelected = (option: DropdownOptionType) => {
    switch (option.value) {
      case "minHeight":
        setMinHeightSet(true);
        setShowMinHeight(true);
        break;
      case "maxHeight":
        setMaxHeightSet(true);
        setShowMaxHeight(true);
        break;
      default:
        console.error("height option not recognized");
        break;
    }
  };

  // Direct Tailwind Class Addition

  const [addClassOpen, setAddClassOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const listRef = useRef(null);

  const handleSearch = (value: string) => {
    if (searchTerm === value) {
      return;
    }

    setSearchTerm(value);
    if (listRef.current) {
      // @ts-expect-error TS2339
      listRef.current.scrollTo(0);
    }
  };

  type EnhancedClassType = ClassType & {
    custom?: boolean;
    score: number;
  };

  const filteredProperties = useMemo(() => {
    const properties: EnhancedClassType[] = TAILWIND_PROPERTIES.map((cls) => {
      let score = 0;

      if (!searchTerm) {
        score = 1; // So that all items are considered when there's no searchTerm
      } else {
        const term = searchTerm.toLowerCase();

        if (cls.tailwind.toLowerCase() === term) {
          score += 10;
        } else if (cls.tailwind.toLowerCase().startsWith(term)) {
          score += 5;
        } else if (cls.tailwind.toLowerCase().includes(term)) {
          score += 3;
        }

        if (cls.css.toLowerCase().includes(term)) {
          score += 1;
        }
      }

      return { ...cls, score };
    })
      .sort((a, b) => b.score - a.score) // Sort items with higher scores first
      .filter((item) => item.score > 0); // Filter out non-matching items

    if (
      searchTerm.length > 4 &&
      (properties.length === 0 || properties[0].score !== 10)
    ) {
      const customProperty: EnhancedClassType = {
        tailwind: searchTerm,
        css: "",
        custom: true,
        score: 10,
      };

      // Add custom property to the top of the list
      properties.unshift(customProperty);
    }

    return properties;
  }, [searchTerm, addClassOpen]);

  const Row = useCallback(
    ({ index, style }) => {
      const codeRef = useRef(null);

      const highlight = () => {
        if (codeRef.current) {
          hljs.highlightBlock(codeRef.current);
        }
      };
      const cls = filteredProperties[index];
      return (
        <CommandItem
          key={cls.tailwind}
          onSelect={() => {
            setAddClassOpen(false);
            addTailwindClass(cls);
          }}
          className="!h-7"
          style={style}
        >
          <div className="h-full w-full">
            <div className="h-full w-full flex flex-row justify-between items-center gap-0.5">
              <div className="font-medium flex-shrink-0 nowrap space-x-2">
                {cls.custom && (
                  <span className="text-secondary-800 italic">Add Custom</span>
                )}
                <span className="text-text-primary text-xs font-normal">
                  {cls.tailwind}
                </span>
              </div>
              <div className="flex flex-shrink-0 flex-col">
                <HoverCard onOpenChange={highlight}>
                  <HoverCardTrigger asChild>
                    <div className="flex flex-col h-full">
                      {cls.css
                        .split("\n")
                        .map((cssLine: string, index: number) => {
                          return (
                            <div
                              key={index}
                              className="leading-tight text-right flex-shrink w-fit text-text-tertiary text-xs font-normal line-clamp-1"
                            >
                              {cssLine}
                            </div>
                          );
                        })}
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="w-80"
                    side="bottom"
                    avoidCollisions={false}
                  >
                    <div className="space-y-1 text-xs">
                      <h4 className="font-semibold">{cls.tailwind}</h4>
                      <pre>
                        <code ref={codeRef} className="css">
                          {cls.css}
                        </code>
                      </pre>{" "}
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>
          </div>
        </CommandItem>
      );
    },
    [filteredProperties]
  );

  // Opacity section

  const [showStyleSection, setShowStyleSection] = useState(false);
  const opacityProperties = useTailwindProperties(PropertyType.OPACITY);

  const [opacity, setOpacity] = useState("");
  const [opacitySet, setOpacitySet] = useState(
    cssValues["opacity"].set || false
  );

  useEffect(() => {
    setShowStyleSection(
      opacitySet ||
        backgroundColorSet ||
        backgroundImageSet ||
        borderRadiusSet ||
        rotationSet ||
        borderSet
    );
  }, [
    selectedElement,
    opacitySet,
    backgroundColorSet,
    backgroundImageSet,
    borderRadiusSet,
    rotationSet,
    borderSet,
  ]);

  useEffect(() => {
    setOpacitySet(cssValues["opacity"].set || false);
  }, [cssValues["opacity"].set]);

  const [fillSet, setFillSet] = useState(false);

  useEffect(() => {
    setFillSet(backgroundColorSet || backgroundImageSet);
  }, [backgroundImageSet, backgroundColorSet]);

  useEffect(() => {
    const opacity = cssValues["opacity"].evaluatedValue;

    let opacityVal;

    if (!opacity || opacity === "none") {
      opacityVal = 100;
    } else {
      opacityVal = Math.round(Number(opacity) * 100);
    }

    setOpacity(opacityVal.toString());
  }, [cssValues["opacity"].evaluatedValue]);

  // Render all the sections

  const isFlex =
    (display === "flex" || recentClassesCache["flex"] === true) &&
    recentClassesCache["flex"] !== false;

  let errorText;

  if (selectedElement.isEmpty() || selectedElement.isStoryboard()) {
    errorText = "Select an element to edit styles";
  } else if (!selectedElement.codebaseId) {
    errorText = "Can't find element in codebase, please select another element";
  } else if (multiselectedElements?.length) {
    errorText = "Can't edit styling for multiple elements at once";
  }

  if (errorText) {
    return (
      <div className="px-4 py-2 flex flex-row justify-center items-center">
        <div className="absolute top-1/3 flex flex-col justify-center border-dashed border border-secondary-600 p-4 m-2">
          <div className="flex flex-row text-secondary-900 items-center justify-center space-x-2">
            <CursorArrowIcon className="size-3 shrink-0" />
            <div className="text-secondary-900 text-2xs font-medium">
              {errorText}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-xs relative" ref={containerRef}>
      <ComboboxContainer>
        <div className="px-4 py-4 space-y-2">
          <>
            <Popover open={addClassOpen} onOpenChange={setAddClassOpen}>
              <PopoverTrigger asChild style={{ zIndex: 1000 }}>
                <div
                  className={cn(
                    "group py-2 px-4 hover:bg-secondary-300 cursor-pointer rounded-md flex flex-row justify-between items-center border border-transparent bg-secondary-200"
                  )}
                  data-tooltip-content="Add Tailwind class"
                  data-tooltip-id="tooltip"
                  data-tooltip-place="bottom"
                >
                  <div className="space-x-1 flex-row flex font-semibold text-secondary-900 text-2xs text-center">
                    <img
                      height={16}
                      width={16}
                      src="/images/tailwind-logo.svg"
                      alt="Add a Tailwind class"
                    />

                    <span>Add Class</span>
                  </div>
                  <PlusIcon className="group-hover:text-white" />
                </div>
              </PopoverTrigger>
              <PopoverContent
                className="w-96 h-[400px] z-[1004] text-2xs p-1.5 rounded-xl"
                side="right"
                align="start"
                onInteractOutside={() => setAddClassOpen(false)}
              >
                <Command shouldFilter={false} loop={false}>
                  <CommandInput
                    placeholder="Search class"
                    onValueChange={handleSearch}
                    rootClassName="mb-1"
                  />
                  <List
                    ref={listRef}
                    height={40 * 12}
                    itemCount={filteredProperties.length}
                    itemSize={40}
                    width="100%"
                  >
                    {Row}
                  </List>
                </Command>
              </PopoverContent>
            </Popover>
            <Popover open={showModifiers} onOpenChange={setShowModifiers}>
              <PopoverTrigger asChild style={{ zIndex: 1000 }}>
                <div
                  className={cn(
                    "group py-2 px-4 hover:bg-secondary-300 cursor-pointer rounded-md flex flex-row justify-between items-center border border-transparent",
                    modifiers.length > 0
                      ? "bg-gradient-to-r from-primary-100 to-primary-0 hover:border-white"
                      : "bg-secondary-200"
                  )}
                  data-tooltip-content={`${totalModifierTally} other style${
                    totalModifierTally > 1 ? "s" : ""
                  } defined using modifiers`}
                  data-tooltip-id="tooltip"
                  data-tooltip-hidden={!totalModifierTally}
                  data-tooltip-place="bottom"
                >
                  <div className="space-x-1 flex-row flex font-semibold text-secondary-900 text-2xs text-center">
                    {modifiers.length > 0 ? (
                      <p className="flex space-x-1 items-center justify-center flex-row leading-none">
                        <span className="font-black text-xs">
                          :{modifiers.join(":")}
                        </span>
                      </p>
                    ) : (
                      <>
                        <img
                          height={16}
                          width={16}
                          src="/images/tailwind-logo.svg"
                          alt="Add a Tailwind Modifier"
                        />

                        <span>
                          <span>{totalModifierTally ? "Select" : "Add"}</span>{" "}
                          Modifier
                        </span>
                      </>
                    )}
                  </div>
                  {totalModifierTally > 0 ? (
                    <div
                      className={cn(
                        "rounded-full h-5 text-3xs text-center flex justify-center items-center ml-1 w-5",
                        modifiers.length > 0
                          ? "bg-primary-900 text-secondary-0"
                          : "bg-secondary-600 text-white"
                      )}
                    >
                      <span>{totalModifierTally}</span>
                    </div>
                  ) : (
                    <PlusIcon className="group-hover:text-white" />
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent
                className="p-0 w-72 h-[400px] z-[1004] text-2xs rounded-xl"
                side="right"
                align="start"
                onInteractOutside={() => setShowModifiers(false)}
              >
                <Command className="max-h-[400px] rounded-xl p-1.5">
                  <CommandInput
                    value={modifiersInput}
                    onValueChange={setModifiersInput}
                    placeholder="Search for a modifier"
                  />
                  <CommandList className="max-h-[400px] rounded-xl">
                    <CommandEmpty>Add {modifiersInput}</CommandEmpty>
                    <CommandGroup heading="Custom">
                      <CommandItem
                        className="text-xs"
                        onSelect={() => addModifier(modifiersInput)}
                        disabled={!modifiersInput}
                      >
                        <span className="text-text-secondary">
                          {modifiersInput.length > 0 &&
                          !ALL_MODIFIERS_SET.has(modifiersInput)
                            ? modifiersInput
                            : "custom:modifier"}
                        </span>
                      </CommandItem>
                    </CommandGroup>
                    {Object.keys(ALL_MODIFIERS).map((modifier) => {
                      const modifiersInGroup = ALL_MODIFIERS[modifier];
                      return (
                        <CommandGroup
                          className="text-xs"
                          heading={modifier}
                          key={modifier}
                        >
                          {modifiersInGroup.map((modifier) => {
                            const count = modifierTally[modifier] || 0;
                            return (
                              <CommandItem
                                key={modifier}
                                className="text-xs flex flex-row"
                                onSelect={() => addModifier(modifier)}
                              >
                                <span>{modifier}</span>
                                {count > 0 && (
                                  <div
                                    className={cn(
                                      "rounded-full bg-secondary-600 text-text-primary h-4 text-3xs text-center flex justify-center items-center ml-1 w-4"
                                    )}
                                  >
                                    <span>{count}</span>
                                  </div>
                                )}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {darkEnabled && !modifiers.includes("dark") && (
              <div
                className="bg-gradient-to-r from-secondary-0 to-secondary-100 px-3 py-2 rounded-sm text-2xs text-secondary-900 font-medium cursor-pointer border-dashed border-primary-300 border"
                onClick={() => addModifier("dark")}
              >
                This element is in <span className="font-bold">dark</span> mode,
                use <span className="font-bold">dark:</span> to edit those
                styles.
              </div>
            )}
            {modifiers.length > 0 && (
              <div className="flex flex-row gap-x-2 gap-y-1 w-full flex-wrap justify-start content-center items-start">
                {[...modifiers].map((modifier, index) => {
                  const count = modifierTally[modifier] || 0;
                  return (
                    <div
                      key={modifier}
                      className="min-w-4 min-h-4 group hover:bg-secondary-600 hover:text-white transition-color duration-300 ease-in-out cursor-pointer px-2 py-0.5 flex justify-center space-x-0.5 items-center rounded-sm text-secondary-900 font-medium text-2xs bg-secondary-400"
                      onClick={() => removeModifier(modifier)}
                      data-tooltip-id="tooltip"
                      data-tooltip-content="Remove modifier"
                      data-tooltip-place="bottom"
                    >
                      <span>{modifier}</span>
                      {count > 0 && <span> ({count})</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        </div>
        <hr></hr>
        <div className="px-4 py-2 flex flex-col gap-y-2 relative">
          <div className="flex flex-row justify-between items-center">
            <div
              className={cn(
                "text-xs font-semibold",
                !showPositionSection && "text-muted-foreground"
              )}
            >
              Position
            </div>
            <div
              className={
                "flex flex-col items-center justify-center w-1/12" +
                (showPositionSection ? " opacity-0" : "")
              }
              onClick={() => {
                setShowPositionSection(true);
              }}
            >
              <ClickableIcon>
                <PlusIcon />
              </ClickableIcon>
            </div>
          </div>
          {/* Position */}
          {showPositionSection && (
            <>
              <>
                <div className="grid grid-cols-7 items-center pl-4 pr-2 gap-x-3 gap-y-2">
                  <div className="col-span-3">
                    <div className="text-xs text-secondary-800 font-medium">
                      Position
                    </div>
                  </div>
                  <div className="col-span-4">
                    <DraggableInput
                      value={position}
                      // @ts-expect-error TS2322
                      evaluatedValue={cssValues["position"]?.evaluatedValue}
                      setValue={setPosition}
                      labelContent={"P"}
                      comboboxOptions={positionProperties}
                      onComboboxSelected={(option) => {
                        addTailwindClass(option);
                      }}
                      onSliderValueUpdate={(option) => {
                        addTailwindClassInstantOnly(option);
                      }}
                      draggable={false}
                      onClear={() => removeStyles("position")}
                      set={positionSet}
                      setSet={setPositionSet}
                      tooltipContent="Position"
                    />
                  </div>
                  {position !== "static" && (
                    <>
                      <div className="col-span-3">
                        <div className="text-xs text-secondary-800 font-medium">
                          Top
                        </div>
                      </div>
                      <div className="col-span-4">
                        <DraggableInput
                          value={top}
                          // @ts-expect-error TS2322
                          evaluatedValue={cssValues["top"]?.evaluatedValue}
                          setValue={setTop}
                          labelContent={"T"}
                          disabled={position === "static"}
                          comboboxOptions={topProperties}
                          onComboboxSelected={(option) => {
                            addTailwindClass(option);
                          }}
                          onSliderValueUpdate={(option) => {
                            addTailwindClassInstantOnly(option);
                          }}
                          onClear={() => removeStyles("top")}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `top-[${input}]`,
                              css: `top: ${input}`,
                            };
                          }}
                          set={topSet}
                          setSet={setTopSet}
                          min={0}
                          step={1}
                          fallback={{ number: 0, unit: "px" }}
                          tooltipContent="Left"
                        />
                      </div>
                    </>
                  )}
                  {position !== "static" && position !== "sticky" && (
                    <>
                      <div className="col-span-3">
                        <div className="text-xs text-secondary-800 font-medium">
                          Left
                        </div>
                      </div>
                      <div className="col-span-4">
                        <DraggableInput
                          value={left}
                          // @ts-expect-error TS2322
                          evaluatedValue={cssValues["left"]?.evaluatedValue}
                          setValue={setLeft}
                          labelContent={"L"}
                          comboboxOptions={leftProperties}
                          disabled={position === "static"}
                          onComboboxSelected={(option) => {
                            addTailwindClass(option);
                          }}
                          onSliderValueUpdate={(option) => {
                            addTailwindClassInstantOnly(option);
                          }}
                          onClear={() => removeStyles("left")}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `left-[${input}]`,
                              css: `left: ${input}`,
                            };
                          }}
                          set={leftSet}
                          setSet={setLeftSet}
                          min={0}
                          step={1}
                          fallback={{ number: 0, unit: "px" }}
                          tooltipContent="Left"
                        />
                      </div>

                      <div className="col-span-3">
                        <div className="text-xs text-secondary-800 font-medium">
                          Right
                        </div>
                      </div>
                      <div className="col-span-4">
                        <DraggableInput
                          value={right}
                          // @ts-expect-error TS2322
                          evaluatedValue={cssValues["right"]?.evaluatedValue}
                          setValue={setRight}
                          labelContent={"R"}
                          comboboxOptions={rightProperties}
                          disabled={position === "static"}
                          onComboboxSelected={(option) => {
                            addTailwindClass(option);
                          }}
                          onSliderValueUpdate={(option) => {
                            addTailwindClassInstantOnly(option);
                          }}
                          onClear={() => removeStyles("right")}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `right-[${input}]`,
                              css: `right: ${input}`,
                            };
                          }}
                          set={rightSet}
                          setSet={setRightSet}
                          min={0}
                          step={1}
                          fallback={{ number: 0, unit: "px" }}
                          tooltipContent="Right"
                        />
                      </div>
                      <div className="col-span-3">
                        <div className="text-xs text-secondary-800 font-medium">
                          Bottom
                        </div>
                      </div>
                      <div className="col-span-4">
                        <DraggableInput
                          value={bottom}
                          // @ts-expect-error TS2322
                          evaluatedValue={cssValues["bottom"]?.evaluatedValue}
                          setValue={setBottom}
                          labelContent={"B"}
                          disabled={position === "static"}
                          comboboxOptions={bottomProperties}
                          onComboboxSelected={(option) => {
                            addTailwindClass(option);
                          }}
                          onSliderValueUpdate={(option) => {
                            addTailwindClassInstantOnly(option);
                          }}
                          onClear={() => removeStyles("bottom")}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `bottom-[${input}]`,
                              css: `bottom: ${input}`,
                            };
                          }}
                          set={bottomSet}
                          setSet={setBottomSet}
                          min={0}
                          step={1}
                          fallback={{ number: 0, unit: "px" }}
                          tooltipContent="Bottom"
                        />
                      </div>
                    </>
                  )}
                </div>
              </>
            </>
          )}
        </div>
        <hr></hr>
        <div className="px-4 py-2 flex flex-col gap-y-2 relative">
          <div className="flex flex-row justify-between items-center relative">
            <div
              className={cn(
                "text-xs font-semibold",
                !showSizingSection && "text-muted-foreground"
              )}
            >
              Size
            </div>
            <div
              className={
                "flex flex-col items-center justify-center w-1/12" +
                (showSizingSection &&
                widthDropdownOptions.length === 0 &&
                heightDropdownOptions.length === 0
                  ? " opacity-0"
                  : "")
              }
              onClick={() => {
                // If the sizing section is not shown, show it
                if (!showSizingSection) {
                  setShowSizingSection(true);
                  return;
                }

                if (
                  widthDropdownOptions.length > 0 ||
                  heightDropdownOptions.length > 0
                ) {
                  // If the sizing section is shown, show the dropdowns
                  setShowSizingSectionDropdown(true);
                }
              }}
            >
              <ClickableIcon>
                <PlusIcon />
              </ClickableIcon>
            </div>
            <DropdownInput
              visible={showSizingSectionDropdown}
              setVisible={setShowSizingSectionDropdown}
              dropdownOptions={[
                ...widthDropdownOptions,
                ...heightDropdownOptions,
              ]}
              onDropdownSelected={(option) => {
                handleWidthDropdownSelected(option);
                handleHeightDropdownSelected(option);
                setShowSizingSectionDropdown(false);
              }}
            />
          </div>

          {/* Width & Height */}
          {showSizingSection && (
            <div className="grid grid-cols-7 items-center pl-4 pr-2 gap-x-3 gap-y-2">
              <div className="col-span-3">
                <div className="text-xs text-secondary-800 font-semibold">
                  Width
                </div>
              </div>
              <div className="col-span-4">
                <DraggableInput
                  value={width}
                  // @ts-expect-error TS2322
                  evaluatedValue={cssValues["width"]?.evaluatedValue}
                  setValue={setWidth}
                  labelContent={"W"}
                  comboboxOptions={widthProperties}
                  dropdownOptions={widthDropdownOptions}
                  onDropdownSelected={(option) => {
                    handleWidthDropdownSelected(option);
                  }}
                  onComboboxSelected={(option) => {
                    addTailwindClass(option);
                  }}
                  onSliderValueUpdate={(option) => {
                    addTailwindClassInstantOnly(option);
                  }}
                  onClear={() => removeStyles("width")}
                  buildArbitraryClass={(input: string) => {
                    return { tailwind: `w-[${input}]`, css: `width: ${input}` };
                  }}
                  set={widthSet}
                  setSet={setWidthSet}
                  min={0}
                  step={1}
                  tooltipContent="Width"
                />
              </div>
              {/* Min/Max Width section */}
              {(showMinWidth || showMaxWidth) && (
                <>
                  {showMinWidth && (
                    <>
                      <div className="col-span-3">
                        <div className="text-xs text-secondary-800 font-medium">
                          Min width
                        </div>
                      </div>

                      <div className="col-span-4">
                        <DraggableInput
                          value={minWidth}
                          // @ts-expect-error TS2322
                          evaluatedValue={
                            cssValues["min-width"]?.evaluatedValue
                          }
                          setValue={setMinWidth}
                          labelContent={<MinWidthIcon />}
                          comboboxOptions={minWidthProperties}
                          onComboboxSelected={(option) => {
                            addTailwindClass(option);
                          }}
                          onSliderValueUpdate={(option) => {
                            addTailwindClassInstantOnly(option);
                          }}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `min-w-[${input}]`,
                              css: `min-width: ${input}`,
                            };
                          }}
                          onClear={() => removeStyles(["min-width"])}
                          set={minWidthSet}
                          setSet={setMinWidthSet}
                          min={0}
                          step={1}
                          tooltipContent="Minimum width"
                        />
                      </div>
                    </>
                  )}
                  {showMaxWidth && (
                    <>
                      <div className="col-span-3">
                        <div className="text-xs text-secondary-800 font-medium">
                          Max width
                        </div>
                      </div>
                      <div className="col-span-4">
                        <DraggableInput
                          value={maxWidth}
                          // @ts-expect-error TS2322
                          evaluatedValue={
                            cssValues["max-width"]?.evaluatedValue
                          }
                          setValue={setMaxWidth}
                          labelContent={<MaxWidthIcon />}
                          comboboxOptions={maxWidthProperties}
                          onComboboxSelected={(option) => {
                            addTailwindClass(option);
                          }}
                          onSliderValueUpdate={(option) => {
                            addTailwindClassInstantOnly(option);
                          }}
                          onClear={() => removeStyles(["max-width"])}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `max-w-[${input}]`,
                              css: `max-width: ${input}`,
                            };
                          }}
                          set={maxWidthSet}
                          setSet={setMaxWidthSet}
                          min={0}
                          step={1}
                          tooltipContent="Maximum width"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="col-span-3">
                <div className="text-xs text-secondary-800 font-semibold">
                  Height
                </div>
              </div>
              <div className="col-span-4">
                <DraggableInput
                  value={height}
                  // @ts-expect-error TS2322
                  evaluatedValue={cssValues["height"]?.evaluatedValue}
                  setValue={setHeight}
                  labelContent={"H"}
                  comboboxOptions={heightProperties}
                  dropdownOptions={heightDropdownOptions}
                  onDropdownSelected={(option) =>
                    handleHeightDropdownSelected(option)
                  }
                  onComboboxSelected={(option) => {
                    addTailwindClass(option);
                  }}
                  onSliderValueUpdate={(option) => {
                    addTailwindClassInstantOnly(option);
                  }}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `h-[${input}]`,
                      css: `height: ${input}`,
                    };
                  }}
                  set={heightSet}
                  setSet={setHeightSet}
                  onClear={() => removeStyles("height")}
                  min={0}
                  step={1}
                  tooltipContent="Height"
                />
              </div>
              {/* Min/Max Height Section */}
              {(showMinHeight || showMaxHeight) && (
                <>
                  {showMinHeight && (
                    <>
                      <div className="col-span-3">
                        <div className="text-xs text-secondary-800 font-medium">
                          Min height
                        </div>
                      </div>
                      <div className="col-span-4">
                        <DraggableInput
                          value={minHeight}
                          // @ts-expect-error TS2322
                          evaluatedValue={
                            cssValues["min-height"]?.evaluatedValue
                          }
                          setValue={setMinHeight}
                          labelContent={<MinWidthIcon className="rotate-90" />}
                          comboboxOptions={minHeightProperties}
                          onComboboxSelected={(option) => {
                            addTailwindClass(option);
                          }}
                          onSliderValueUpdate={(option) => {
                            addTailwindClassInstantOnly(option);
                          }}
                          onClear={() => removeStyles(["min-height"])}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `min-h-[${input}]`,
                              css: `min-height: ${input}`,
                            };
                          }}
                          set={minHeightSet}
                          setSet={setMinHeightSet}
                          min={0}
                          step={1}
                          tooltipContent="Minimum height"
                        />
                      </div>
                    </>
                  )}
                  {showMaxHeight && (
                    <>
                      <div className="col-span-3">
                        <div className="text-xs text-secondary-800 font-medium">
                          Max height
                        </div>
                      </div>
                      <div className="col-span-4">
                        <DraggableInput
                          value={maxHeight}
                          // @ts-expect-error TS2322
                          evaluatedValue={
                            cssValues["max-height"]?.evaluatedValue
                          }
                          setValue={setMaxHeight}
                          labelContent={<MaxWidthIcon className="rotate-90" />}
                          comboboxOptions={maxHeightProperties}
                          onComboboxSelected={(option) => {
                            addTailwindClass(option);
                          }}
                          onSliderValueUpdate={(option) => {
                            addTailwindClassInstantOnly(option);
                          }}
                          onClear={() => removeStyles(["max-height"])}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `max-h-[${input}]`,
                              css: `max-height: ${input}`,
                            };
                          }}
                          min={0}
                          step={1}
                          set={maxHeightSet}
                          setSet={setMaxHeightSet}
                          tooltipContent="Maximum height"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Flex Grow & Shrink */}
              {showFlexGrowShrinkSection &&
                (parentFlexDirection === "row" ? (
                  <>
                    <div className="col-span-3">
                      <div className="text-xs text-secondary-800 font-medium">
                        Grow
                      </div>
                    </div>
                    <div className="col-span-4">
                      <DraggableInput
                        // @ts-expect-error TS2322
                        value={
                          parentFlexDirection === "row" ? flexGrowShrink : ""
                        }
                        evaluatedValue={""}
                        setValue={(value) => {}}
                        set={parentFlexDirection === "row"}
                        setSet={(set: boolean) => {}}
                        labelContent={
                          horizontalFlexGrowShrinkOptions.find(
                            (option) => option.value === flexGrowShrink
                          )?.icon || horizontalFlexGrowShrinkOptions[0].icon
                        }
                        // @ts-expect-error TS2322
                        dropdownOptions={
                          parentFlexDirection === "row" &&
                          horizontalFlexGrowShrinkOptions
                        }
                        onDropdownSelected={(option) => {
                          handleFlexGrowShrinkDropdownSelected(option);
                        }}
                        disabled={parentFlexDirection !== "row"}
                        draggable={false}
                        tooltipContent={"Horizontal Resizing"}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-3">
                      <div className="text-xs text-secondary-800 font-medium">
                        Resizing
                      </div>
                    </div>
                    <div className="col-span-4">
                      <DraggableInput
                        // @ts-expect-error TS2322
                        value={
                          parentFlexDirection === "column" ? flexGrowShrink : ""
                        }
                        evaluatedValue=""
                        setValue={(value) => {}}
                        set={parentFlexDirection === "column"}
                        setSet={(set: boolean) => {}}
                        labelContent={
                          verticalFlexGrowShrinkOptions.find(
                            (option) => option.value === flexGrowShrink
                          )?.icon || verticalFlexGrowShrinkOptions[0].icon
                        }
                        // @ts-expect-error TS2322
                        dropdownOptions={
                          parentFlexDirection === "column" &&
                          verticalFlexGrowShrinkOptions
                        }
                        onDropdownSelected={(option) => {
                          handleFlexGrowShrinkDropdownSelected(option);
                        }}
                        disabled={parentFlexDirection !== "column"}
                        draggable={false}
                        tooltipContent={"Vertical Resizing"}
                      />{" "}
                    </div>
                  </>
                ))}
            </div>
          )}
        </div>
        <hr></hr>
        <div className="px-4 py-2">
          <div className="flex flex-col space-y-2">
            <div className="flex flex-row justify-between items-center">
              <div
                className={cn(
                  "text-xs font-semibold",
                  !showSpacingSection && "text-muted-foreground"
                )}
              >
                Spacing
              </div>
              <div
                className={
                  "flex flex-col items-center justify-center w-1/12" +
                  (showSpacingSection ? " opacity-0" : "")
                }
                onClick={() => {
                  setShowSpacingSection(true);
                }}
              >
                <ClickableIcon>
                  <PlusIcon />
                </ClickableIcon>
              </div>
            </div>
            {showSpacingSection && (
              <>
                <div className="grid grid-cols-21 items-center pl-4 pr-2 gap-x-3 gap-y-2">
                  <>
                    {!paddingExpanded && (
                      <>
                        <div className="col-span-9">
                          <div className="text-xs text-secondary-800 font-semibold">
                            Padding
                          </div>
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            value={paddingHorizontal}
                            evaluatedValue={evaluatedPaddingHorizontal}
                            set={paddingHorizontalSet}
                            setSet={setPaddingHorizontalSet}
                            setValue={setPaddingHorizontal}
                            labelContent={<PaddingHorizontalIcon />}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `px-[${input}]`,
                                css: `padding-left: ${input}; padding-right: ${input}`,
                              };
                            }}
                            comboboxOptions={paddingHorizontalProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            step={0.25}
                            min={0}
                            onComboboxSelected={(option) => addPadding(option)}
                            onClear={() =>
                              addPaddingWithStyle({
                                "padding-left": null,
                                "padding-right": null,
                              })
                            }
                            tooltipContent="Horizontal padding"
                          />
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            value={paddingVertical}
                            evaluatedValue={evaluatedPaddingVertical}
                            set={paddingVerticalSet}
                            setSet={setPaddingVerticalSet}
                            setValue={setPaddingVertical}
                            labelContent={<PaddingVerticalIcon />}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `py-[${input}]`,
                                css: `padding-top: ${input}; padding-bottom: ${input}`,
                              };
                            }}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            step={0.25}
                            min={0}
                            comboboxOptions={paddingVerticalProperties}
                            onComboboxSelected={(option) => addPadding(option)}
                            tooltipContent="Vertical padding"
                            onClear={() =>
                              addPaddingWithStyle({
                                "padding-top": null,
                                "padding-bottom": null,
                              })
                            }
                          />
                        </div>
                        <div className="col-span-2 flex flex-col items-center justify-center">
                          <div
                            className={
                              "flex p-1 items-center justify-center border border-transparent hover:border-secondary-800 cursor-pointer w-3 h-3 " +
                              `${paddingExpanded ? "bg-secondary-500" : ""}`
                            }
                            onClick={() =>
                              paddingExpandEnabled &&
                              setPaddingExpanded((prev) => !prev)
                            }
                          >
                            <ExpandSettingPadding className="w-3 h-3" />
                          </div>
                        </div>
                      </>
                    )}
                    {paddingExpanded && (
                      <>
                        <div className="col-span-9">
                          <div className="text-xs text-secondary-800 font-semibold">
                            Padding X
                          </div>
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            labelContent={<PaddingLeftIcon />}
                            value={paddingLeft}
                            // @ts-expect-error TS2322
                            evaluatedValue={
                              cssValues["padding-left"]?.evaluatedValue
                            }
                            setValue={setPaddingLeft}
                            comboboxOptions={paddingLeftProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onComboboxSelected={(option) => addPadding(option)}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pl-[${input}]`,
                                css: `padding-left: ${input};`,
                              };
                            }}
                            onClear={() =>
                              addPaddingWithStyle({ "padding-left": null })
                            }
                            step={0.25}
                            min={0}
                            set={paddingLeftSet}
                            setSet={setPaddingLeftSet}
                            tooltipContent="Left padding"
                          />
                        </div>

                        <div className="col-span-5">
                          <DraggableInput
                            labelContent={<PaddingRightIcon />}
                            value={paddingRight}
                            // @ts-expect-error TS2322
                            evaluatedValue={
                              cssValues["padding-right"]?.evaluatedValue
                            }
                            setValue={setPaddingRight}
                            comboboxOptions={paddingRightProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onComboboxSelected={(option) => addPadding(option)}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pr-[${input}]`,
                                css: `padding-right: ${input};`,
                              };
                            }}
                            onClear={() =>
                              addPaddingWithStyle({ "padding-right": null })
                            }
                            step={0.25}
                            min={0}
                            set={paddingRightSet}
                            setSet={setPaddingRightSet}
                            tooltipContent="Right padding"
                          />
                        </div>
                        <div className="col-span-2 flex flex-col items-center justify-center">
                          <div
                            className={
                              "flex p-1 items-center justify-center border border-transparent hover:border-secondary-800 cursor-pointer w-3 h-3 " +
                              `${paddingExpanded ? "bg-secondary-500" : ""}`
                            }
                            onClick={() =>
                              paddingExpandEnabled &&
                              setPaddingExpanded((prev) => !prev)
                            }
                          >
                            <ExpandSettingPadding className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="col-span-9">
                          <div className="text-xs text-secondary-800 font-semibold">
                            Padding Y
                          </div>
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            labelContent={<PaddingTopIcon />}
                            value={paddingTop}
                            // @ts-expect-error TS2322
                            evaluatedValue={
                              cssValues["padding-top"]?.evaluatedValue
                            }
                            setValue={setPaddingTop}
                            comboboxOptions={paddingTopProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onComboboxSelected={(option) => addPadding(option)}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pt-[${input}]`,
                                css: `padding-top: ${input};`,
                              };
                            }}
                            onClear={() =>
                              addPaddingWithStyle({ "padding-top": null })
                            }
                            step={0.25}
                            min={0}
                            set={paddingTopSet}
                            setSet={setPaddingTopSet}
                            tooltipContent="Top padding"
                          />
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            labelContent={<PaddingBottomIcon />}
                            value={paddingBottom}
                            // @ts-expect-error TS2322
                            evaluatedValue={
                              cssValues["padding-bottom"]?.evaluatedValue
                            }
                            setValue={setPaddingBottom}
                            comboboxOptions={paddingBottomProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onComboboxSelected={(option) => addPadding(option)}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pb-[${input}]`,
                                css: `padding-bottom: ${input};`,
                              };
                            }}
                            onClear={() =>
                              addPaddingWithStyle({ "padding-bottom": null })
                            }
                            step={0.25}
                            min={0}
                            set={paddingBottomSet}
                            setSet={setPaddingBottomSet}
                            tooltipContent="Bottom padding"
                          />
                        </div>
                      </>
                    )}
                  </>
                </div>
                <div className="grid grid-cols-21 items-center pl-4 pr-2 gap-x-3 gap-y-2">
                  <>
                    {!marginExpanded && (
                      <>
                        <div className="col-span-9">
                          <div className="text-xs text-secondary-800 font-semibold">
                            Margin
                          </div>
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            value={marginHorizontal}
                            evaluatedValue={evaluatedMarginHorizontal}
                            set={marginHorizontalSet}
                            setSet={setMarginHorizontalSet}
                            setValue={setMarginHorizontal}
                            labelContent={<ColumnSpacingIcon />}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `mx-[${input}]`,
                                css: `margin-left: ${input}; margin-right: ${input}`,
                              };
                            }}
                            comboboxOptions={marginHorizontalProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onClear={() =>
                              addMarginWithStyle({
                                "margin-left": null,
                                "margin-right": null,
                              })
                            }
                            step={0.25}
                            min={0}
                            onComboboxSelected={(option) => addMargin(option)}
                            tooltipContent="Horizontal margin"
                          />
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            value={marginVertical}
                            evaluatedValue={evaluatedMarginVertical}
                            set={marginVerticalSet}
                            setSet={setMarginVerticalSet}
                            setValue={setMarginVertical}
                            labelContent={<RowSpacingIcon />}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `my-[${input}]`,
                                css: `margin-top: ${input}; margin-bottom: ${input}`,
                              };
                            }}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onClear={() =>
                              addMarginWithStyle({
                                "margin-top": null,
                                "margin-bottom": null,
                              })
                            }
                            step={0.25}
                            min={0}
                            comboboxOptions={marginVerticalProperties}
                            onComboboxSelected={(option) => addMargin(option)}
                            tooltipContent="Vertical margin"
                          />
                        </div>
                        <div className="col-span-2 flex flex-col items-center justify-center">
                          <div
                            className={
                              "flex p-1 items-center justify-center border border-transparent hover:border-secondary-800 cursor-pointer w-3 h-3 " +
                              `${marginExpanded ? "bg-secondary-500" : ""}`
                            }
                            onClick={() =>
                              marginExpandEnabled &&
                              setMarginExpanded((prev) => !prev)
                            }
                          >
                            <ExpandSettingPadding className="w-3 h-3" />
                          </div>
                        </div>
                      </>
                    )}
                    {marginExpanded && (
                      <>
                        <div className="col-span-9">
                          <div className="text-xs text-secondary-800 font-semibold">
                            Margin X
                          </div>
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            labelContent={
                              <PinLeftIcon width={13} height={13} />
                            }
                            value={marginLeft}
                            // @ts-expect-error TS2322
                            evaluatedValue={
                              cssValues["margin-left"]?.evaluatedValue
                            }
                            setValue={setMarginLeft}
                            comboboxOptions={marginLeftProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onComboboxSelected={(option) => addMargin(option)}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `ml-[${input}]`,
                                css: `margin-left: ${input};`,
                              };
                            }}
                            onClear={() =>
                              addMarginWithStyle({ "margin-left": null })
                            }
                            step={0.25}
                            min={0}
                            set={marginLeftSet}
                            setSet={setMarginLeftSet}
                            tooltipContent="Left margin"
                          />
                        </div>

                        <div className="col-span-5">
                          <DraggableInput
                            labelContent={
                              <PinRightIcon width={13} height={13} />
                            }
                            value={marginRight}
                            // @ts-expect-error TS2322
                            evaluatedValue={
                              cssValues["margin-right"]?.evaluatedValue
                            }
                            setValue={setMarginRight}
                            comboboxOptions={marginRightProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onComboboxSelected={(option) => addMargin(option)}
                            onClear={() =>
                              addMarginWithStyle({ "margin-right": null })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `mr-[${input}]`,
                                css: `margin-right: ${input};`,
                              };
                            }}
                            step={0.25}
                            min={0}
                            set={marginRightSet}
                            setSet={setMarginRightSet}
                            tooltipContent="Right margin"
                          />
                        </div>
                        <div className="col-span-2 flex flex-col items-center justify-center">
                          <div
                            className={
                              "flex p-1 items-center justify-center border border-transparent hover:border-secondary-800 cursor-pointer w-3 h-3 " +
                              `${marginExpanded ? "bg-secondary-500" : ""}`
                            }
                            onClick={() =>
                              marginExpandEnabled &&
                              setMarginExpanded((prev) => !prev)
                            }
                          >
                            <ExpandSettingPadding className="w-3 h-3" />
                          </div>
                        </div>

                        <div className="col-span-9">
                          <div className="text-xs text-secondary-800 font-semibold">
                            Margin Y
                          </div>
                        </div>
                        <div className="col-span-5">
                          <DraggableInput
                            labelContent={<PinTopIcon width={13} height={13} />}
                            value={marginTop}
                            // @ts-expect-error TS2322
                            evaluatedValue={
                              cssValues["margin-top"]?.evaluatedValue
                            }
                            setValue={setMarginTop}
                            comboboxOptions={marginTopProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onComboboxSelected={(option) => addMargin(option)}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `mt-[${input}]`,
                                css: `margin-top: ${input};`,
                              };
                            }}
                            onClear={() =>
                              addMarginWithStyle({ "margin-top": null })
                            }
                            step={0.25}
                            min={0}
                            set={marginTopSet}
                            setSet={setMarginTopSet}
                            tooltipContent="Top margin"
                          />
                        </div>

                        <div className="col-span-5">
                          <DraggableInput
                            labelContent={
                              <PinBottomIcon width={13} height={13} />
                            }
                            value={marginBottom}
                            // @ts-expect-error TS2322
                            evaluatedValue={
                              cssValues["margin-bottom"]?.evaluatedValue
                            }
                            setValue={setMarginBottom}
                            comboboxOptions={marginBottomProperties}
                            onSliderValueUpdate={(option) =>
                              addTailwindClassInstantOnly(option)
                            }
                            onComboboxSelected={(option) => addMargin(option)}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `mb-[${input}]`,
                                css: `margin-bottom: ${input};`,
                              };
                            }}
                            onClear={() =>
                              addMarginWithStyle({ "margin-bottom": null })
                            }
                            step={0.25}
                            min={0}
                            set={marginBottomSet}
                            setSet={setMarginBottomSet}
                            tooltipContent="Bottom margin"
                          />
                        </div>
                      </>
                    )}
                  </>
                </div>
              </>
            )}
          </div>
        </div>
        <hr></hr>
        <div className="px-4 py-2 space-y-3">
          <div className="flex flex-row justify-between items-center">
            <div
              className={cn(
                "text-xs font-semibold",
                !isFlex && "text-muted-foreground"
              )}
            >
              Layout
            </div>
            <div
              className={
                "flex flex-col items-center justify-center w-1/12" +
                (isFlex ? " opacity-0" : "")
              }
              onClick={() => {
                if (!isFlex) {
                  setDisplay("flex");
                  addTailwindClass({
                    tailwind: "flex",
                    css: "display: flex;",
                  });
                }
              }}
            >
              <ClickableIcon>
                <PlusIcon />
              </ClickableIcon>
            </div>
          </div>
          {isFlex && (
            <>
              <div className="flex flex-col justify-center pr-2 gap-x-3 gap-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-xs w-1/3 text-text-secondary font-normal">
                    Align
                  </div>
                  <div className="w-2/3">
                    <AutoLayoutSelector
                      selected={layoutIndex}
                      setSelected={(selected) => {
                        addLayout(selected);
                        setLayoutIndex(selected);
                      }}
                      row={flexDirection?.startsWith("row")}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="text-xs w-1/3 text-text-secondary font-normal">
                    Direction
                  </div>
                  <div className="w-2/3">
                    <DiscreteInput
                      options={FLEX_DIRECTION_OPTIONS}
                      onOptionSelected={(discreteOption) => {
                        setFlexDirection(discreteOption.key);
                        addTailwindClass(discreteOption.option);
                      }}
                      selected={flexDirection}
                      set={flexDirectionSet}
                      onDeselect={() => {
                        removeStyles("flex-direction");
                        setFlexDirection(null);
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="text-xs w-1/3 text-text-secondary font-normal">
                    Gap
                  </div>
                  <div className="w-2/3">
                    {flexDirection?.startsWith("row") ? (
                      <DraggableInput
                        labelContent={
                          <ColumnSpacingIcon className="text-text-tertiary" />
                        }
                        value={columnGap}
                        // @ts-expect-error TS2322
                        evaluatedValue={cssValues["column-gap"]?.evaluatedValue}
                        set={columnGapSet}
                        setSet={setColumnGapSet}
                        setValue={setColumnGap}
                        comboboxOptions={columnGapProperties}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `gap-[${input}]`,
                            css: `column-gap: ${input}`,
                          };
                        }}
                        step={0.5}
                        min={0}
                        fallback={{ number: 0, unit: "px" }}
                        onSliderValueUpdate={(option) =>
                          addTailwindClassInstantOnly(option)
                        }
                        onClear={() => {
                          removeStyles("column-gap");
                          setColumnGap(null);
                        }}
                        onComboboxSelected={(option) =>
                          addTailwindClass(option)
                        }
                        tooltipContent="Column gap"
                      />
                    ) : (
                      <DraggableInput
                        labelContent={
                          <RowSpacingIcon className="text-text-tertiary" />
                        }
                        value={rowGap}
                        // @ts-expect-error TS2322
                        evaluatedValue={cssValues["row-gap"]?.evaluatedValue}
                        set={rowGapSet}
                        setSet={setRowGapSet}
                        setValue={setRowGap}
                        comboboxOptions={rowGapProperties}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `gap-[${input}]`,
                            css: `row-gap: ${input};`,
                          };
                        }}
                        step={0.5}
                        min={0}
                        fallback={{ number: 0, unit: "px" }}
                        onSliderValueUpdate={(option) =>
                          addTailwindClassInstantOnly(option)
                        }
                        onClear={() => {
                          removeStyles("row-gap");
                          setRowGap(null);
                        }}
                        onComboboxSelected={(option) =>
                          addTailwindClass(option)
                        }
                        tooltipContent="Row gap"
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="text-xs w-1/3 text-text-secondary font-normal">
                    Children
                  </div>
                  <div className="w-2/3">
                    <DiscreteInput
                      options={FLEX_WRAP_OPTIONS}
                      onOptionSelected={(discreteOption) => {
                        setFlexWrap(discreteOption.key);
                        addTailwindClass(discreteOption.option);
                      }}
                      selected={flexWrap}
                      set={flexWrapSet}
                      onDeselect={() => {
                        setFlexWrap(null);
                        removeStyles("flex-wrap");
                      }}
                    />
                  </div>
                </div>
                {flexWrap !== "nowrap" && (
                  <>
                    <div className="flex items-center justify-between gap-4">
                      <div className="text-xs w-1/3 text-text-secondary font-normal">
                        Align children
                      </div>
                      <div className="w-2/3">
                        <DiscreteInput
                          options={ALIGN_CONTENT_OPTIONS}
                          onOptionSelected={(discreteOption) => {
                            setAlignContent(discreteOption.key);
                            addTailwindClass(discreteOption.option);
                          }}
                          selected={alignContent}
                          set={alignContentSet}
                          onDeselect={() => {
                            setAlignContent(null);
                            removeStyles("align-content");
                          }}
                          className="grid grid-cols-3"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between gap-4">
                  <div className="text-xs w-1/3 text-text-secondary font-normal">
                    Align
                  </div>
                  <div className="w-2/3">
                    <DiscreteInput
                      options={ALIGN_ITEM_OPTIONS}
                      selected={alignItems}
                      onOptionSelected={(option) => {
                        addTailwindClass(option.option);
                        setAlignItems(option.key);
                      }}
                      onDeselect={() => {
                        setAlignItems(null);
                        removeStyles("align-items");
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="text-xs w-1/3 text-text-secondary font-normal">
                    Justify
                  </div>
                  <div className="w-2/3">
                    <DiscreteInput
                      options={JUSTIFY_CONTENT_OPTIONS}
                      selected={justifyContent}
                      onOptionSelected={(option) => {
                        addTailwindClass(option.option);
                        setJustifyContent(option.key);
                      }}
                      onDeselect={(option) => {
                        setJustifyContent(null);
                        removeStyles("justify-content");
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <hr></hr>
        <div className="px-4 py-2">
          <div className="flex flex-row justify-between items-center">
            <h1
              className={cn(
                "text-xs font-semibold",
                !showStyleSection && "text-muted-foreground"
              )}
            >
              Styles
            </h1>
            <div
              className={
                "flex flex-col items-center justify-center w-1/12" +
                (showStyleSection ? " opacity-0" : "")
              }
              onClick={() => {
                setShowStyleSection(true);
              }}
            >
              <ClickableIcon>
                <PlusIcon />
              </ClickableIcon>
            </div>
          </div>
          {showStyleSection && (
            <div className="grid grid-cols-14 items-center pl-4 pr-2 gap-x-3 gap-y-2">
              <div className="col-span-6">
                <div className="text-xs text-secondary-800 font-semibold">
                  Opacity
                </div>
              </div>
              <div className="col-span-8">
                <DraggableInput
                  value={opacity}
                  evaluatedValue={opacity}
                  setValue={setOpacity}
                  labelContent={<OpacityIcon />}
                  comboboxOptions={opacityProperties}
                  onComboboxSelected={(option) => {
                    addTailwindClass(option);
                  }}
                  onSliderValueUpdate={(option) => {
                    addTailwindClassInstantOnly(option);
                  }}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `opacity-[${input}%]`,
                      css: `opacity: ${input}%`,
                    };
                  }}
                  set={opacitySet}
                  setSet={setOpacitySet}
                  min={0}
                  max={100}
                  step={1}
                  tooltipContent="Opacity"
                  onClear={() => {
                    removeStyles("opacity");
                  }}
                />
              </div>
              <div className="col-span-6">
                <div className="text-xs text-secondary-800 font-semibold">
                  Fill
                </div>
              </div>
              <div className="col-span-8" ref={fillModalRef}>
                <>
                  {!fillSet ? (
                    <EmptyInputState
                      onClick={() =>
                        setModal({
                          type: StylingModalType.Fill,
                          anchor: fillModalRef,
                        })
                      }
                    />
                  ) : (
                    <div
                      className="cursor-pointer flex flex-row items-center relative border bg-secondary-200 rounded-md h-8 px-2 hover:border-secondary-600 border-transparent"
                      onClick={() => {
                        setModal({
                          type: StylingModalType.Fill,
                          anchor: fillModalRef,
                        });
                      }}
                    >
                      <div
                        className="flex flex-row py-2 px-2 select-none"
                        style={{
                          backgroundColor:
                            cssValues["background-color"]?.evaluatedValue ||
                            "#fff",
                          backgroundImage: cssValues["background-image"]
                            .evaluatedValue
                            ? `${cssValues["background-image"]?.evaluatedValue}`
                            : "none",
                        }}
                      ></div>
                      <div className="text-2xs px-1 border-none outline-none w-full font-medium bg-transparent text-white">
                        {cssValues[
                          "background-image"
                        ]?.evaluatedValue?.includes("linear-gradient")
                          ? "Gradient"
                          : cssValues[
                              "background-image"
                            ]?.evaluatedValue?.includes("url")
                          ? "Image"
                          : "Solid"}
                      </div>
                    </div>
                  )}
                </>
              </div>
              <div className="col-span-6">
                <div className="text-xs text-secondary-800 font-semibold">
                  Radius
                </div>
              </div>
              <div className="col-span-8 flex flex-row justify-between items-center gap-x-2">
                <DraggableInput
                  value={borderRadius}
                  evaluatedValue={evaluatedBorderRadius}
                  setValue={(val) => {
                    setBorderRadius(val);
                  }}
                  labelContent={<CornerTopLeftIcon />}
                  disabled={radiusExpanded}
                  onComboboxSelected={(option) => addTailwindClass(option)}
                  onSliderValueUpdate={(option) =>
                    addTailwindClassInstantOnly(option)
                  }
                  comboboxOptions={borderRadiusProperties}
                  buildArbitraryClass={(input) => {
                    return {
                      tailwind: `rounded-[${input}]`,
                      css: `border-top-left-radius: ${input}; border-top-right-radius: ${input}; border-bottom-left-radius: ${input}; border-bottom-right-radius: ${input}`,
                    };
                  }}
                  set={borderRadiusSet}
                  setSet={setBorderRadiusSet}
                  tooltipContent="Border radius"
                  onClear={() => removeStyles(["border-radius"])}
                />
                <div
                  className={cn(
                    "flex items-center justify-center border border-transparent hover:border-secondary-700 cursor-pointer p-1 w-4 h-4",
                    radiusExpanded && "bg-secondary-300"
                  )}
                  onClick={() =>
                    radiusExpandEnabled && setRadiusExpanded((prev) => !prev)
                  }
                >
                  <CornersIcon />
                </div>
              </div>
              {/* Individual Radius */}
              {radiusExpanded && (
                <>
                  <div className="col-span-6 flex justify-end">
                    <div className="py-2 px-2 select-none border border-secondary-500 rounded-md text-secondary-600">
                      {React.createElement(radiusIcons[activeRadius])}
                    </div>
                  </div>
                  <div className="col-span-8">
                    <div className="flex flex-row items-center w-full gap-x-3 border border-transparent">
                      <div>
                        <DraggableInput
                          labelContent={null}
                          value={topLeftRadius}
                          // @ts-expect-error TS2322
                          evaluatedValue={
                            cssValues["border-top-left-radius"]?.evaluatedValue
                          }
                          setValue={setTopLeftRadius}
                          onInputFocus={() => setActiveRadius(0)}
                          comboboxOptions={topLeftRadiusProperties}
                          onComboboxSelected={(option) =>
                            addTailwindClass(option)
                          }
                          buildArbitraryClass={(input) => {
                            return {
                              tailwind: `rounded-tl-[${input}]`,
                              css: `border-top-left-radius: ${input};`,
                            };
                          }}
                          set={topLeftRadiusSet}
                          setSet={setTopLeftRadiusSet}
                          tooltipContent="Top left radius"
                          onClear={() =>
                            removeStyles(["border-top-left-radius"])
                          }
                        />
                      </div>
                      <div>
                        <DraggableInput
                          labelContent={null}
                          value={topRightRadius}
                          // @ts-expect-error TS2322
                          evaluatedValue={
                            cssValues["border-top-right-radius"]?.evaluatedValue
                          }
                          setValue={setTopRightRadius}
                          onInputFocus={() => setActiveRadius(1)}
                          comboboxOptions={topRightRadiusProperties}
                          onComboboxSelected={(option) =>
                            addTailwindClass(option)
                          }
                          buildArbitraryClass={(input) => {
                            return {
                              tailwind: `rounded-tr-[${input}]`,
                              css: `border-top-right-radius: ${input};`,
                            };
                          }}
                          set={topRightRadiusSet}
                          setSet={setTopRightRadiusSet}
                          tooltipContent="Top right radius"
                          onClear={() =>
                            removeStyles(["border-top-right-radius"])
                          }
                        />
                      </div>
                      <div>
                        <DraggableInput
                          labelContent={null}
                          value={bottomRightRadius}
                          // @ts-expect-error TS2322
                          evaluatedValue={
                            cssValues["border-bottom-right-radius"]
                              ?.evaluatedValue
                          }
                          setValue={setBottomRightRadius}
                          onInputFocus={() => setActiveRadius(2)}
                          comboboxOptions={bottomRightRadiusProperties}
                          onComboboxSelected={(option) =>
                            addTailwindClass(option)
                          }
                          buildArbitraryClass={(input) => {
                            return {
                              tailwind: `rounded-br-[${input}]`,
                              css: `border-bottom-right-radius: ${input};`,
                            };
                          }}
                          set={bottomRightRadiusSet}
                          setSet={setBottomRightRadiusSet}
                          tooltipContent="Bottom right radius"
                          onClear={() =>
                            removeStyles(["border-bottom-right-radius"])
                          }
                        />
                      </div>
                      <div>
                        <DraggableInput
                          labelContent={null}
                          value={bottomLeftRadius}
                          // @ts-expect-error TS2322
                          evaluatedValue={
                            cssValues["border-bottom-left-radius"]
                              ?.evaluatedValue
                          }
                          setValue={setBottomLeftRadius}
                          onInputFocus={() => setActiveRadius(3)}
                          comboboxOptions={bottomLeftRadiusProperties}
                          onComboboxSelected={(option) =>
                            addTailwindClass(option)
                          }
                          buildArbitraryClass={(input) => {
                            return {
                              tailwind: `rounded-bl-[${input}]`,
                              css: `border-bottom-left-radius: ${input};`,
                            };
                          }}
                          set={bottomLeftRadiusSet}
                          setSet={setBottomLeftRadiusSet}
                          tooltipContent="Bottom left radius"
                          onClear={() =>
                            removeStyles(["border-bottom-left-radius"])
                          }
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="col-span-6">
                <div className="text-xs text-secondary-800 font-semibold">
                  Rotation
                </div>
              </div>
              <div className="col-span-8">
                <DraggableInput
                  value={rotation}
                  evaluatedValue={rotation}
                  setValue={setRotation}
                  labelContent={<AngleIcon />}
                  set={rotationSet}
                  setSet={setRotationSet}
                  onComboboxSelected={(option) => {
                    addTailwindClass(option);
                  }}
                  onSliderValueUpdate={(option) => {
                    addTailwindClassInstantOnly(option);
                  }}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `rotate-[${input}deg]`,
                      css: `transform: rotate(${input}deg)`,
                    };
                  }}
                  onClear={() => removeStyles(["transform"])}
                  comboboxOptions={rotationProperties}
                  tooltipContent="Rotation"
                />
              </div>
              <div className="col-span-6">
                <div className="text-xs text-secondary-800 font-medium">
                  Border
                </div>
              </div>
              <div className="col-span-8" ref={borderModalRef}>
                {borderSet ? (
                  <div
                    className="cursor-pointer flex flex-row items-center relative border bg-secondary-200 rounded-md h-8 px-2 hover:border-secondary-600 border-transparent"
                    onClick={() => {
                      setModal({
                        type: StylingModalType.Border,
                        anchor: borderModalRef,
                      });
                    }}
                  >
                    <div
                      className="flex flex-row py-2 px-2 select-none"
                      style={{ backgroundColor: borderColor || "#fff" }}
                    ></div>
                    <div className="text-2xs px-1 border-none outline-none w-full font-medium bg-transparent text-white">
                      {borderStyle || "Solid"}
                    </div>
                  </div>
                ) : (
                  <EmptyInputState
                    onClick={() => {
                      setModal({
                        type: StylingModalType.Border,
                        anchor: borderModalRef,
                      });
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <hr></hr>
        <div className="px-4 py-2">
          <div className="flex flex-col space-y-2">
            <div className="flex flex-row justify-between items-center">
              <h1
                className={cn(
                  "text-xs font-semibold",
                  !showTextSection && "text-muted-foreground"
                )}
              >
                Text
              </h1>
              <div
                className={
                  "flex flex-col items-center justify-center w-1/12" +
                  (showTextSection ? " opacity-0" : "")
                }
                onClick={() => {
                  setShowTextSection(true);
                }}
              >
                <ClickableIcon>
                  <PlusIcon />
                </ClickableIcon>
              </div>
            </div>
            {showTextSection && (
              <div className="grid grid-cols-7 items-center pl-4 pr-2 gap-x-3 gap-y-2">
                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Font weight
                  </div>
                </div>
                <div className="col-span-4">
                  <DraggableInput
                    value={fontWeight}
                    // @ts-expect-error TS2322
                    evaluatedValue={cssValues["font-weight"]?.evaluatedValue}
                    setValue={setFontWeight}
                    set={fontWeightSet}
                    setSet={setFontWeightSet}
                    labelContent={<FontBoldIcon />}
                    min={100}
                    max={900}
                    step={100}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `font-[${input}]`,
                        css: `font-weight: ${input};`,
                      };
                    }}
                    onSliderValueUpdate={(option) =>
                      addTailwindClassInstantOnly(option)
                    }
                    buildOptionPreview={buildFontWeightColorPreview}
                    comboboxOptions={fontWeightProperties}
                    onComboboxSelected={(option) => addTailwindClass(option)}
                    tooltipContent="Font weight"
                    onClear={() => {
                      removeStyles("font-weight");
                    }}
                  />
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Font size
                  </div>
                </div>
                <div className="col-span-4">
                  <DraggableInput
                    value={fontSize}
                    // @ts-expect-error TS2322
                    evaluatedValue={cssValues["font-size"]?.evaluatedValue}
                    setValue={setFontSize}
                    set={fontSizeSet}
                    setSet={setFontSizeSet}
                    labelContent={<FontSizeIcon />}
                    min={0}
                    step={0.25}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `text-[${input}]`,
                        css: `font-size: ${input};`,
                      };
                    }}
                    onSliderValueUpdate={(option) =>
                      addTailwindClassInstantOnly(option)
                    }
                    comboboxOptions={fontSizeProperties}
                    onComboboxSelected={(option) => addTailwindClass(option)}
                    tooltipContent="Font size"
                    onClear={() => {
                      removeStyles("font-size");
                    }}
                  />
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Line height
                  </div>
                </div>
                <div className="col-span-4">
                  <DraggableInput
                    value={lineHeight}
                    // @ts-expect-error TS2322
                    evaluatedValue={cssValues["line-height"]?.evaluatedValue}
                    set={lineHeightSet}
                    setSet={setLineHeightSet}
                    setValue={setLineHeight}
                    labelContent={<LineHeightIcon />}
                    min={0}
                    step={0.25}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `leading-[${input}]`,
                        css: `line-height: ${input};`,
                      };
                    }}
                    onSliderValueUpdate={(option) =>
                      addTailwindClassInstantOnly(option)
                    }
                    comboboxOptions={lineHeightProperties}
                    onComboboxSelected={(option) => addTailwindClass(option)}
                    tooltipContent="Line height"
                    onClear={() => {
                      removeStyles("line-height");
                    }}
                  />
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Letter spacing
                  </div>
                </div>
                <div className="col-span-4">
                  <DraggableInput
                    value={letterSpacing}
                    // @ts-expect-error TS2322
                    evaluatedValue={cssValues["letter-spacing"]?.evaluatedValue}
                    set={letterSpacingSet}
                    setSet={setLetterSpacingSet}
                    setValue={setLetterSpacing}
                    labelContent={<LetterSpacingIcon />}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `tracking-[${input}]`,
                        css: `letter-spacing: ${input};`,
                      };
                    }}
                    comboboxOptions={letterSpacingProperties}
                    onSliderValueUpdate={(option) =>
                      addTailwindClassInstantOnly(option)
                    }
                    onComboboxSelected={(option) => addTailwindClass(option)}
                    step={0.05}
                    tooltipContent="Letter spacing"
                    onClear={() => {
                      removeStyles("letter-spacing");
                    }}
                  />
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Color
                  </div>
                </div>
                <div className="col-span-4" ref={textColorModalRef}>
                  {textColorSet ? (
                    <div
                      className="cursor-pointer flex flex-row items-center relative border bg-secondary-200 rounded-md h-8 px-2 hover:border-secondary-600 border-transparent"
                      onClick={() => {
                        setModal({
                          type: StylingModalType.TextColor,
                          anchor: textColorModalRef,
                        });
                      }}
                    >
                      <div
                        className="flex flex-row py-2 px-2 select-none"
                        style={{ backgroundColor: textColor || "#fff" }}
                      ></div>
                      <div className="text-2xs px-1 border-none outline-none w-full font-medium bg-transparent text-white">
                        {textColor || "Solid"}
                      </div>
                    </div>
                  ) : (
                    <EmptyInputState
                      onClick={() => {
                        setModal({
                          type: StylingModalType.TextColor,
                          anchor: textColorModalRef,
                        });
                      }}
                    />
                  )}
                </div>

                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Alignment
                  </div>
                </div>
                <div className="col-span-4">
                  <div className="w-full">
                    <DiscreteInput
                      options={TEXT_ALIGN_OPTIONS}
                      onOptionSelected={(discreteOption) => {
                        setTextAlign(discreteOption.key);
                        addTailwindClass(discreteOption.option);
                      }}
                      selected={textAlign}
                      set={textAlignSet}
                      onDeselect={() => {
                        removeStyles("text-align");
                        setTextAlign(null);
                      }}
                    />
                  </div>
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Font
                  </div>
                </div>
                <div className="col-span-4">
                  <DraggableInput
                    draggable={false}
                    value={fontFamilyDecorated}
                    evaluatedValue={""}
                    set={fontFamilySet}
                    setValue={setFontFamily}
                    setSet={setFontFamilySet}
                    labelContent={<FontFamilyIcon />}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `font-[${input}]`,
                        css: `font-family: ${input};`,
                      };
                    }}
                    comboboxOptions={fontFamilyProperties}
                    onComboboxSelected={(option) => addTailwindClass(option)}
                    tooltipContent="Font family"
                    onClear={() => {
                      removeStyles("font-family");
                    }}
                  />
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Decoration
                  </div>
                </div>
                <div className="col-span-4">
                  <div className="w-full">
                    <DiscreteInput
                      selected={textDecorationLine}
                      set={textDecorationLineSet}
                      onOptionSelected={(discreteOption) => {
                        setTextDecorationLine(discreteOption.key);
                        addTailwindClass(discreteOption.option);
                      }}
                      onDeselect={() => {
                        setTextDecorationLine("none");
                        removeStyles("text-decoration-line");
                      }}
                      options={TEXT_DECORATION_LINE_OPTIONS}
                    />
                  </div>
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-secondary-800 font-medium">
                    Italic
                  </div>
                </div>
                <div className="col-span-4">
                  <div className="w-1/5">
                    <ToggleInput
                      toggled={fontStyle === "italic"}
                      tooltipContent={
                        fontStyle === "italic" ? "Italic" : "Normal"
                      }
                      labelContent={<FontItalicIcon />}
                      onToggle={(italic: boolean) => {
                        if (italic) {
                          setFontStyle("italic");
                          addTailwindClass({
                            tailwind: "italic",
                            css: "font-style: italic;",
                          });
                        } else {
                          setFontStyle("normal");
                          removeStyles("font-style");
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <hr></hr>
        <div className="px-4 py-2">
          <div className="flex flex-col w-full space-y-2">
            <div className="flex flex-row justify-between items-center">
              <h1
                className={cn(
                  "text-xs font-semibold",
                  // @ts-expect-error TS18047
                  effects.length === 0 && "text-muted-foreground"
                )}
              >
                Effects
              </h1>
              <div
                className="flex flex-col items-center justify-center w-1/12"
                onClick={() => {
                  addEffect();
                }}
              >
                <ClickableIcon>
                  <PlusIcon />
                </ClickableIcon>
              </div>
            </div>
            {/*
             // @ts-expect-error TS18047 */}
            {effects.length > 0 && (
              <div className="grid grid-cols-7 items-center pl-4 pr-2 gap-x-3 gap-y-2">
                {/*
                 // @ts-expect-error TS18047 */}
                {effects.map((effect, index) => (
                  <Fragment key={index}>
                    <div className="col-span-3 text-xs text-secondary-800 font-medium">
                      {effect.type}
                    </div>
                    <div className="col-span-4 flex justify-between flex-row">
                      <div className="flex flex-row grow">
                        <DraggableInput
                          key={index}
                          value={effect.value}
                          evaluatedValue={effect.value}
                          setValue={(value) => {
                            effect.value = value;
                          }}
                          set={true}
                          setSet={(set: boolean) => {}}
                          labelContent={effect.icon}
                          dropdownOptions={effectDropdownOptions()}
                          onDropdownSelected={(option) => {
                            if (effect.value) {
                              removeTailwindClass(effect.value);
                            }
                            onEffectDropdownSelected(option, index);
                          }}
                          comboboxOptions={effect.properties}
                          onComboboxSelected={(option) => {
                            effect.value = option.tailwind;
                            addTailwindClass(option);
                          }}
                          draggable={false}
                          tooltipContent={effect.type}
                        />
                      </div>
                      <div className="w-1/12 flex justify-center items-center">
                        <ClickableIcon
                          onClick={() => {
                            if (effect.value) {
                              removeTailwindClass(effect.value);
                            }
                            removeEffect(index);
                          }}
                        >
                          <MinusIcon width={10} height={10} />
                        </ClickableIcon>
                      </div>
                    </div>
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
        <hr />
        <div className="px-4 py-2 flex flex-col space-y-2">
          <div className="flex flex-col w-full">
            {unknownTailwindClassesToShow.map((cls: string) => (
              <div
                key={cls}
                className="text-2xs flex justify-between items-center w-full"
              >
                <div className="text-left">{cls}</div>
                <div className="w-1/12 flex flex-col justif-center items-center">
                  <ClickableIcon onClick={() => removeTailwindClass(cls)}>
                    <MinusIcon width={12} height={12} />
                  </ClickableIcon>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="h-24" />
      </ComboboxContainer>
      <ModalContainer
        className={cn("!w-[250px] !border-none !gap-y-3")}
        title={modalTypeToTitle[modal.type]}
        visible={modal.type !== StylingModalType.None}
        onDismiss={() =>
          setModal({
            type: StylingModalType.None,
          })
        }
        anchorRef={modal.anchor}
        containerRef={containerRef}
      >
        <ComboboxContainer>
          {modal.type === StylingModalType.TextColor && (
            <div className="grid grid-cols-1">
              <CustomColorPicker
                value={textColor}
                evaluatedValue={cssValues["color"].evaluatedValue}
                set={textColorSet}
                setSet={setTextColorSet}
                setValue={setTextColor}
                buildArbitraryClass={(input: string) => {
                  return {
                    tailwind: `text-[${input}]`,
                    css: `color: ${input};`,
                  };
                }}
                onValueUpdate={(option) => addTailwindClassInstantOnly(option)}
                comboboxOptions={textColorProperties}
                onComboboxSelected={(option) => addTailwindClass(option)}
                buildOptionPreview={buildTextColorPreview}
                tooltipContent="Text color"
                onClear={() => {
                  removeStyles("color");
                }}
              />
            </div>
          )}
          {modal.type === StylingModalType.Border && (
            <div className="grid grid-cols-7 items-center gap-x-3 gap-y-2">
              <div className="col-span-7">
                <CustomColorPicker
                  value={borderColor}
                  evaluatedValue={borderColor}
                  setValue={setBorderColor}
                  buildArbitraryClass={(input) => {
                    return {
                      tailwind: `border-[${input}]`,
                      css: `border-color: ${input};`,
                    };
                  }}
                  set={borderColorSet}
                  setSet={setBorderColorSet}
                  onValueUpdate={(option) =>
                    addTailwindClassInstantOnly(option)
                  }
                  comboboxOptions={borderColorProperties}
                  onComboboxSelected={(option) => addTailwindClass(option)}
                  // buildOptionPreview={buildBackgroundColorPreview}
                  tooltipContent="Border color"
                  onClear={() => {
                    removeStyles("border-color");
                  }}
                />
              </div>
              <div className="col-span-3">
                <div className="text-xs text-secondary-800 font-medium">
                  Style
                </div>
              </div>
              <div className="col-span-4">
                <DraggableInput
                  labelContent={<BorderStyleIcon />}
                  draggable={false}
                  onSliderValueUpdate={(option) =>
                    addTailwindClassInstantOnly(option)
                  }
                  value={borderStyle}
                  evaluatedValue={evaluatedBorderStyle}
                  set={borderStyleSet}
                  setSet={setBorderStyleSet}
                  setValue={setBorderStyle}
                  comboboxOptions={borderStyleProperties}
                  tooltipContent="Border style"
                  onComboboxSelected={(option) => addTailwindClass(option)}
                />
              </div>
              <div className="col-span-3">
                <div className="text-xs text-secondary-800 font-medium">
                  Width
                </div>
              </div>
              <div className="col-span-2">
                <DraggableInput
                  onSliderValueUpdate={(option) =>
                    addTailwindClassInstantOnly(option)
                  }
                  labelContent={
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      className="bi bi-border-width rotate-180"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M0 3.5A.5.5 0 0 1 .5 3h15a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5H.5a.5.5 0 0 1-.5-.5v-2zm0 5A.5.5 0 0 1 .5 8h15a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H.5a.5.5 0 0 1-.5-.5v-1zm0 4a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5z" />
                    </svg>
                  }
                  min={0}
                  step={0.25}
                  value={borderWidth}
                  evaluatedValue={evaluatedBorderWidth}
                  set={borderWidthSet}
                  setSet={setBorderWidthSet}
                  setValue={setBorderWidth}
                  buildArbitraryClass={(input) => ({
                    tailwind: `border-[${input}]`,
                    css: `border-width: ${input};`,
                  })}
                  comboboxOptions={borderWidthProperties}
                  tooltipContent="Border width"
                  onComboboxSelected={(option: ComboBoxOptionType) => {
                    addTailwindClass(option);
                  }}
                  disabled={borderWidthExpanded}
                />
              </div>
              <div className="col-span-2">
                <DiscreteInput
                  options={BORDER_WIDTH_OPTIONS}
                  selected={borderWidthExpanded ? "individual" : "all"}
                  onOptionSelected={(option) => {
                    if (option.key === "all") {
                      if (borderWidthExpanded) {
                        setBorderWidthExpanded(false);
                        setBorderWidth(borderLeftWidth);
                        changeBorderWidth({
                          "border-width": borderLeftWidth,
                        });
                      }
                    } else {
                      if (!borderWidthExpanded) {
                        setBorderWidthExpanded(true);
                      }
                    }
                  }}
                />
              </div>
              {borderWidthExpanded && (
                <>
                  <div className="col-span-2 col-start-4">
                    <DraggableInput
                      onSliderValueUpdate={(option) =>
                        addTailwindClassInstantOnly(option)
                      }
                      onComboboxSelected={(option) => {
                        changeBorderWidth(
                          transformToApplyCssChangesFormat(option.css)
                        );
                      }}
                      labelContent={<BorderLeftIcon />}
                      min={0}
                      step={0.25}
                      value={borderLeftWidth}
                      // @ts-expect-error TS2322
                      evaluatedValue={
                        cssValues["border-left-width"]?.evaluatedValue
                      }
                      set={borderLeftWidthSet}
                      setSet={setBorderLeftWidthSet}
                      setValue={setBorderLeftWidth}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-l-[${input}]`,
                        css: `border-left-width: ${input};`,
                      })}
                      tooltipContent="Border left width"
                      comboboxOptions={borderLeftWidthProperties}
                    />
                  </div>
                  <div className="col-span-2">
                    <DraggableInput
                      onSliderValueUpdate={(option) =>
                        addTailwindClassInstantOnly(option)
                      }
                      onComboboxSelected={(option) => {
                        changeBorderWidth(
                          transformToApplyCssChangesFormat(option.css)
                        );
                      }}
                      labelContent={<BorderRightIcon />}
                      min={0}
                      step={0.25}
                      value={borderRightWidth}
                      // @ts-expect-error TS2322
                      evaluatedValue={
                        cssValues["border-right-width"]?.evaluatedValue
                      }
                      set={borderRightWidthSet}
                      setSet={setBorderRightWidthSet}
                      setValue={setBorderRightWidth}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-r-[${input}]`,
                        css: `border-right-width: ${input};`,
                      })}
                      tooltipContent="Border right width"
                      comboboxOptions={borderRightWidthProperties}
                    />
                  </div>
                  <div className="col-span-2 col-start-4">
                    <DraggableInput
                      onSliderValueUpdate={(option) =>
                        addTailwindClassInstantOnly(option)
                      }
                      onComboboxSelected={(option) => {
                        changeBorderWidth(
                          transformToApplyCssChangesFormat(option.css)
                        );
                      }}
                      labelContent={<BorderBottomIcon />}
                      min={0}
                      step={0.25}
                      value={borderBottomWidth}
                      // @ts-expect-error TS2322
                      evaluatedValue={
                        cssValues["border-bottom-width"]?.evaluatedValue
                      }
                      set={borderBottomWidthSet}
                      setSet={setBorderBottomWidthSet}
                      setValue={setBorderBottomWidth}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-b-[${input}]`,
                        css: `border-bottom-width: ${input};`,
                      })}
                      tooltipContent="Border bottom width"
                      comboboxOptions={borderBottomWidthProperties}
                    />
                  </div>
                  <div className="col-span-2">
                    <DraggableInput
                      onSliderValueUpdate={(option) =>
                        addTailwindClassInstantOnly(option)
                      }
                      onComboboxSelected={(option) => {
                        changeBorderWidth(
                          transformToApplyCssChangesFormat(option.css)
                        );
                      }}
                      labelContent={<BorderTopIcon />}
                      min={0}
                      step={0.25}
                      value={borderTopWidth}
                      // @ts-expect-error TS2322
                      evaluatedValue={
                        cssValues["border-top-width"]?.evaluatedValue
                      }
                      set={borderTopWidthSet}
                      setSet={setBorderTopWidthSet}
                      setValue={setBorderTopWidth}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-t-[${input}]`,
                        css: `border-top-width: ${input};`,
                      })}
                      tooltipContent="Border top width"
                      comboboxOptions={borderTopWidthProperties}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          {modal.type === StylingModalType.Fill && (
            <FillModal
              classList={classListWithCache}
              selectedElement={selectedElement}
              backgroundColor={backgroundColor}
              backgroundColorSet={backgroundColorSet}
              setBackgroundColor={setBackgroundColor}
              setBackgroundColorSet={setBackgroundColorSet}
              backgroundImage={backgroundImage}
              backgroundImageSet={backgroundImageSet}
              setBackgroundImage={setBackgroundImage}
              setBackgroundImageSet={setBackgroundImageSet}
              addTailwindClass={addTailwindClass}
              addTailwindClassInstantOnly={addTailwindClassInstantOnly}
              removeTailwindClass={removeTailwindClass}
              removeStyles={removeStyles}
              cssValues={cssValues}
            />
          )}
        </ComboboxContainer>
      </ModalContainer>
    </div>
  );
};

export default React.memo(TailwindStylingPanel);
